import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNoshStore } from '../lib/store';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { AvatarSelector } from '../components/ui/avatar-selector';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BadgeCheck, 
  Camera, 
  Crown, 
  Star, 
  User, 
  Heart, 
  Settings, 
  ChevronRight,
  Globe,
  Clock,
  Bell,
  ArrowLeft,
  LogOut,
  X,
  RotateCcw,
  Scale,
  Target,
  MessageSquare,
  ExternalLink
} from 'lucide-react';
import { db } from '../lib/database';
import { useToast } from '../components/ui/use-toast';
import { useMobileSwipeBack } from '../components/ui/mobile-swipe-back';
// import { useLeftEdgeSwipeDisable } from '../hooks/useLeftEdgeSwipeDisable'; // Disabled - causes conflicts with subpage back buttons
import { convertHeight, convertWeight, getBodyStatsLabels } from '../lib/measurement-units';
import { calculateNutritionTargets, getDietaryPatternInfo, type DietaryPreference } from '../lib/nutrition-calculator';
import { DailyNutritionTargets } from '../components/ui/daily-nutrition-targets';
import { useNotifications } from '../hooks/useNotifications';
import { AdminAIUsagePanel } from '../components/AdminAIUsagePanel';
import { CaptchaToggle } from '../components/ui/captcha-toggle';
import { currentAnnouncement, announcementUtils, type AnnouncementItem, type Announcement } from '../config/announcements';
import { announcementConfigManager } from '../lib/announcement-config-manager';
import { AnnouncementEditorModal } from '../components/ui/announcement-editor-modal';
import AIUsageSection from '../components/ui/AIUsageSection';
import AIUsageSectionMobile from '../components/ui/AIUsageSectionMobile';

import ReactDOM from 'react-dom';

// Options arrays used by both mobile and desktop
const dietaryOptions: Array<{value: string, label: string, icon: string, color: string, iconBg: string, textColor: string, tooltip?: string}> = [
  { 
    value: 'balanced', 
    label: 'Balanced Diet', 
    icon: '🍽️', 
    color: 'bg-slate-50 dark:bg-slate-900/20 border-slate-200 dark:border-slate-800/30', 
    iconBg: 'bg-slate-100 dark:bg-slate-800/30', 
    textColor: 'text-slate-700 dark:text-slate-300',
    tooltip: 'Includes: Both animal and plant-based foods.\nMost common globally, varies by region and culture.'
  },
  { 
    value: 'vegetarian', 
    label: 'Vegetarian', 
    icon: '🥬', 
    color: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/30', 
    iconBg: 'bg-green-100 dark:bg-green-800/30', 
    textColor: 'text-green-700 dark:text-green-300',
    tooltip: 'Excludes: Meat, may include eggs and dairy.\nPopular in: India, Europe, growing elsewhere.'
  },
  { 
    value: 'vegan', 
    label: 'Vegan', 
    icon: '🌱', 
    color: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/30', 
    iconBg: 'bg-emerald-100 dark:bg-emerald-800/30', 
    textColor: 'text-emerald-700 dark:text-emerald-300',
    tooltip: 'Excludes: All animal-derived products (meat, dairy, eggs, honey).\nRising in: US, UK, Australia, Germany, urban Asia.'
  },
  { 
    value: 'pescatarian', 
    label: 'Pescatarian', 
    icon: '🐟', 
    color: 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800/30', 
    iconBg: 'bg-cyan-100 dark:bg-cyan-800/30', 
    textColor: 'text-cyan-700 dark:text-cyan-300',
    tooltip: 'Excludes: Land animals, includes fish/seafood.\nPopular in: Japan, Mediterranean countries.'
  },
  { 
    value: 'keto', 
    label: 'Keto / Low-Carb', 
    icon: '🥓', 
    color: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800/30', 
    iconBg: 'bg-purple-100 dark:bg-purple-800/30', 
    textColor: 'text-purple-700 dark:text-purple-300',
    tooltip: 'Focuses on: High fat, low carbohydrate intake.\nUsed for: Weight loss, blood sugar control.'
  },
  { 
    value: 'halal', 
    label: 'Halal', 
    icon: '☪️', 
    color: 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800/30', 
    iconBg: 'bg-teal-100 dark:bg-teal-800/30', 
    textColor: 'text-teal-700 dark:text-teal-300',
    tooltip: 'Follows: Islamic dietary laws.\nNo: Pork, alcohol; requires halal-certified meat.'
  },
  { 
    value: 'kosher', 
    label: 'Kosher', 
    icon: '✡️', 
    color: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800/30', 
    iconBg: 'bg-indigo-100 dark:bg-indigo-800/30', 
    textColor: 'text-indigo-700 dark:text-indigo-300',
    tooltip: 'Follows: Jewish dietary laws.\nRestrictions: No pork/shellfish, separation of dairy and meat.'
  },
  { 
    value: 'gluten-free', 
    label: 'Gluten-Free', 
    icon: '🌾', 
    color: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/30', 
    iconBg: 'bg-amber-100 dark:bg-amber-800/30', 
    textColor: 'text-amber-700 dark:text-amber-300',
    tooltip: 'Excludes: Gluten-containing grains.\nEssential for: Celiac disease; also followed for perceived health benefits.'
  },
  { 
    value: 'paleo', 
    label: 'Paleo', 
    icon: '🦴', 
    color: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/30', 
    iconBg: 'bg-orange-100 dark:bg-orange-800/30', 
    textColor: 'text-orange-700 dark:text-orange-300',
    tooltip: 'Inspired by: Pre-agricultural human diets.\nIncludes: Meats, fish, fruits, vegetables, nuts.\nExcludes: Grains, dairy, legumes, refined sugar, processed food.'
  }
];

const allergyOptions: Array<{value: string, label: string, icon: string, color: string, iconBg: string, textColor: string}> = [
  { value: 'nuts', label: 'Nuts', icon: '🥜', color: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/30', iconBg: 'bg-red-100 dark:bg-red-800/30', textColor: 'text-red-700 dark:text-red-300' },
  { value: 'shellfish', label: 'Shellfish', icon: '🦐', color: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/30', iconBg: 'bg-orange-100 dark:bg-orange-800/30', textColor: 'text-orange-700 dark:text-orange-300' },
  { value: 'eggs', label: 'Eggs', icon: '🥚', color: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800/30', iconBg: 'bg-yellow-100 dark:bg-yellow-800/30', textColor: 'text-yellow-700 dark:text-yellow-300' },
  { value: 'soy', label: 'Soy', icon: '🫘', color: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800/30', iconBg: 'bg-purple-100 dark:bg-purple-800/30', textColor: 'text-purple-700 dark:text-purple-300' },
  { value: 'wheat', label: 'Wheat', icon: '🌾', color: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/30', iconBg: 'bg-amber-100 dark:bg-amber-800/30', textColor: 'text-amber-700 dark:text-amber-300' },
  { value: 'fish', label: 'Fish', icon: '🐠', color: 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800/30', iconBg: 'bg-cyan-100 dark:bg-cyan-800/30', textColor: 'text-cyan-700 dark:text-cyan-300' },
  { value: 'milk', label: 'Milk', icon: '🥛', color: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/30', iconBg: 'bg-blue-100 dark:bg-blue-800/30', textColor: 'text-blue-700 dark:text-blue-300' },
  { value: 'peanuts', label: 'Peanuts', icon: '🥜', color: 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800/30', iconBg: 'bg-rose-100 dark:bg-rose-800/30', textColor: 'text-rose-700 dark:text-rose-300' }
];

// Individual page components for mobile
const AccountInformationPage = ({ onBack }: { onBack: () => void }) => {
  const { user, updateUserName } = useNoshStore();
  const { toast } = useToast();
  const [editableName, setEditableName] = useState(user.name);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Disable mobile swipe back to prevent touch event conflicts with back button
  // The back button provides the navigation functionality
  // useMobileSwipeBack({
  //   enableSwipeBack: true,
  //   customBackHandler: () => {
  //     // Prevent any browser navigation - handle only internal navigation
  //     onBack();
  //     return false; // Explicitly prevent browser back
  //   },
  //   threshold: 80,
  //   edgeWidth: 60
  // });
  
  // Sync editable name when user name changes
  useEffect(() => {
    setEditableName(user.name);
  }, [user.name]);
  
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditableName(e.target.value);
  };
  
  const handleNameBlur = async () => {
    if (editableName !== user.name && editableName.trim() !== '') {
      setIsUpdating(true);
      try {
        await updateUserName(editableName.trim());
        toast({
          title: "Name updated!",
          description: "Your name has been updated successfully.",
          duration: 1500,
        });
      } catch (error) {
        console.error('Error updating name:', error);
        toast({
          title: "Error",
          description: "Failed to update name. Please try again.",
          variant: "destructive",
        });
        setEditableName(user.name); // Revert to original name
      } finally {
        setIsUpdating(false);
      }
    }
  };
  
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur(); // Trigger the blur event to save
    }
  };

  // Handle logout with authentication check
  const handleAccountLogout = async () => {
    try {
      const { handleLogoutRedirect } = await import('../lib/logout-utils');
      
      // Check if user is authenticated
      const { supabase } = await import('../lib/supabase');
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      if (!currentUser) {
        // User is not logged in, redirect using utility
        const { getLogoutRedirectUrl } = await import('../lib/logout-utils');
        window.location.href = getLogoutRedirectUrl();
        return;
      }
      
      // User is authenticated, perform logout using utility
      await handleLogoutRedirect();
    } catch (error) {
      console.error('Error during logout:', error);
      // Fallback: redirect using utility
      const { getLogoutRedirectUrl } = await import('../lib/logout-utils');
      window.location.href = getLogoutRedirectUrl();
    }
  };
  
  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.3 }}
      className="fixed inset-0 bg-white z-50 overflow-y-auto"
    >
      <div className="sticky top-0 bg-white border-b border-gray-200 p-4">
        <div className="flex items-center">
          <button 
            onClick={onBack}
            className="mr-4 p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-semibold">Account Information</h1>
        </div>
      </div>
      
      <div className="p-6 space-y-6">
        <div>
          <h3 className="text-lg font-medium mb-4">Personal Details</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Name</label>
              <input
                type="text"
                value={editableName}
                onChange={handleNameChange}
                onBlur={handleNameBlur}
                onKeyPress={handleKeyPress}
                disabled={isUpdating}
                className="w-full p-3 border border-gray-200 rounded-lg bg-white focus:border-primary focus:ring-1 focus:ring-primary transition-colors disabled:opacity-50"
                placeholder="Enter your name"
              />
              {isUpdating && (
                <p className="text-xs text-gray-500 mt-1">Updating...</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
              <input
                type="email"
                value={user.email}
                className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                readOnly
              />
              <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium mb-4">Account Actions</h3>
          <div className="space-y-3">
            <motion.div
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              className="transition-all duration-200"
            >
              <button
                onClick={handleAccountLogout}
                className="w-full flex items-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-all duration-200"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg">
                    <LogOut className="h-5 w-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium">Logout</p>
                    <p className="text-sm text-red-600 dark:text-red-500">Sign out of your account</p>
                  </div>
                </div>
              </button>
            </motion.div>
          </div>
        </div>

      </div>
    </motion.div>
  );
};

const PreferencesPage = ({ 
  onBack,
  customAllergies,
  setCustomAllergies,
  onRemoveCustomAllergy
}: { 
  onBack: () => void,
  customAllergies: string[],
  setCustomAllergies: (allergies: string[]) => void,
  onRemoveCustomAllergy: (allergy: string) => void
}) => {
  const { user } = useNoshStore();
  const { toast } = useToast();
  const [selectedDietary, setSelectedDietary] = useState<string[]>(
    user.preferences || []
  );
  const [selectedAllergies, setSelectedAllergies] = useState<string[]>(
    user.allergies || []
  );

  // Custom allergies modal state for mobile
  const [showAddAllergyModal, setShowAddAllergyModal] = useState(false);
  const [newAllergyName, setNewAllergyName] = useState('');

  // Disable mobile swipe back to prevent touch event conflicts with back button
  // The back button provides the navigation functionality
  // useMobileSwipeBack({
  //   enableSwipeBack: true,
  //   customBackHandler: () => {
  //     // Prevent any browser navigation - handle only internal navigation
  //     onBack();
  //     return false; // Explicitly prevent browser back
  //   },
  //   threshold: 80,
  //   edgeWidth: 60
  // });

  // Update local state when user data changes
  useEffect(() => {
    setSelectedDietary(user.preferences || []);
    setSelectedAllergies(user.allergies || []);
    
    // Extract custom allergies from user.allergies (ones not in predefined allergyOptions)
    const predefinedAllergyValues = allergyOptions.map(option => option.value);
    const userCustomAllergies = (user.allergies || []).filter(allergy => 
      !predefinedAllergyValues.includes(allergy)
    );
    setCustomAllergies(userCustomAllergies);
  }, [user.preferences, user.allergies, setCustomAllergies]);

  // Simple helper to ensure mobile nav stays hidden (non-aggressive)
  const ensureNavHidden = () => {
    const mobileNav = document.querySelector('.mobile-navigation');
    if (mobileNav) {
      (mobileNav as HTMLElement).style.transform = 'translateY(100%)';
      (mobileNav as HTMLElement).style.transition = 'transform 0.3s ease-in-out';
    }
  };

  // Simple mobile navigation handling without scroll interference
  useEffect(() => {
    const mobileNav = document.querySelector('.mobile-navigation');
    if (mobileNav) {
      (mobileNav as HTMLElement).style.transform = 'translateY(100%)';
      (mobileNav as HTMLElement).style.transition = 'transform 0.3s ease-in-out';
    }

    return () => {
      // Restore mobile navigation on cleanup with better reliability
      setTimeout(() => {
        const mobileNav = document.querySelector('.mobile-navigation');
        if (mobileNav) {
          (mobileNav as HTMLElement).style.transform = 'translateY(0)';
          (mobileNav as HTMLElement).style.transition = 'transform 0.3s ease-in-out';
        }
      }, 50); // Small delay to ensure proper restoration
    };
  }, []);

  // Clean back handler
  const handleBack = () => {
    onBack();
  };

  const toggleDietary = async (value: string) => {
    const newSelection = selectedDietary.includes(value) ? [] : [value];
    setSelectedDietary(newSelection);
    
    try {
      await useNoshStore.getState().updateUserPreferences(newSelection);
      toast({
        title: "Preferences updated!",
        description: `Dietary preference ${newSelection.length > 0 ? 'set to ' + value : 'cleared'}.`,
        duration: 1500,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save preferences. Please try again.",
        variant: "destructive",
        duration: 1500,
      });
      setSelectedDietary(selectedDietary);
    }
  };

  const toggleAllergy = async (value: string) => {
    const newSelection = selectedAllergies.includes(value) 
      ? selectedAllergies.filter(item => item !== value)
      : [...selectedAllergies, value];
    setSelectedAllergies(newSelection);
    
    try {
      await useNoshStore.getState().updateUserAllergies(newSelection);
      toast({
        title: "Allergies updated!",
        description: `${selectedAllergies.includes(value) ? 'Removed' : 'Added'} ${value} allergy.`,
        duration: 1500,
      });
    } catch (error) {
      toast({
        title: "Error", 
        description: "Failed to save allergies. Please try again.",
        variant: "destructive",
        duration: 1500,
      });
      setSelectedAllergies(user.allergies || []);
    }
  };

  // Handle adding custom allergy for mobile
  const handleAddCustomAllergy = () => {
    setShowAddAllergyModal(true);
  };

  const handleSaveCustomAllergy = async () => {
    if (!newAllergyName.trim()) return;
    
    const trimmedName = newAllergyName.trim();
    const newCustomList = [...customAllergies, trimmedName];
    const updatedAllergies = [...selectedAllergies, trimmedName]; // Auto-select the new allergy
    
    setCustomAllergies(newCustomList);
    setSelectedAllergies(updatedAllergies);
    setNewAllergyName('');
    setShowAddAllergyModal(false);
    
    try {
      await useNoshStore.getState().updateUserAllergies(updatedAllergies);
      toast({
        title: "Custom allergy added!",
        description: `Added ${trimmedName} to your allergies.`,
        duration: 1500,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save custom allergy. Please try again.",
        variant: "destructive",
        duration: 1500,
      });
      setCustomAllergies(customAllergies);
      setSelectedAllergies(selectedAllergies);
    }
  };

  const handleCancelCustomAllergy = () => {
    setNewAllergyName('');
    setShowAddAllergyModal(false);
  };

  // Handle removing custom allergy
  const handleRemoveCustomAllergy = async (allergyToRemove: string) => {
    const newCustomList = customAllergies.filter(allergy => allergy !== allergyToRemove);
    const updatedAllergies = selectedAllergies.filter(allergy => allergy !== allergyToRemove);
    
    setCustomAllergies(newCustomList);
    setSelectedAllergies(updatedAllergies);
    
    try {
      await useNoshStore.getState().updateUserAllergies(updatedAllergies);
      toast({
        title: "Custom allergy removed!",
        description: `Removed ${allergyToRemove} from your allergies.`,
        duration: 1500,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove custom allergy. Please try again.",
        variant: "destructive",
        duration: 1500,
      });
      setCustomAllergies(customAllergies);
      setSelectedAllergies(selectedAllergies);
    }
  };

  const PreferenceCard = ({ 
    option, 
    isSelected, 
    onToggle
  }: { 
    option: any, 
    isSelected: boolean, 
    onToggle: () => void
  }) => (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onToggle}
      className={`relative p-3 rounded-2xl border-2 transition-all duration-200 text-left w-full ${
        isSelected
          ? option.color + ' border-transparent shadow-md'
          : 'bg-gray-100 dark:bg-gray-600 border-transparent shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-500'
      }`}
    >
      <div className="flex items-center space-x-3">
        {/* Icon - no animation to prevent flashing */}
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
          isSelected ? option.iconBg : 'bg-gray-100 dark:bg-gray-700'
        }`}>
          <span className="text-lg">{option.icon}</span>
        </div>
        
        {/* Content */}
        <div className="flex-1">
          <h3 className={`font-semibold text-sm transition-colors duration-200 ${
            isSelected ? option.textColor : 'text-gray-900 dark:text-gray-100'
          }`}>
            {option.label}
          </h3>
        </div>
        
        {/* Checkbox indicator */}
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
          isSelected 
            ? 'bg-white border-white shadow-sm' 
            : 'border-gray-300 dark:border-gray-600'
        }`}>
          {isSelected && (
            <div className="w-2.5 h-2.5 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full" />
          )}
        </div>
      </div>
      
      {/* Selection indicator background */}
      {isSelected && (
        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl pointer-events-none" />
      )}
    </motion.button>
  );
  
  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.3 }}
      className="fixed inset-0 bg-gray-50 dark:bg-gray-900 z-50 overflow-y-auto"
      style={{
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain'
      }}
    >
      <div className="p-6 space-y-8 max-w-2xl mx-auto pb-40">
        {/* Dietary Preferences Section with Back Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-2">
              <button 
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
              <h2 className="text-xl font-heading font-bold text-gray-900 dark:text-gray-100">Dietary Preferences</h2>
        </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm text-left">
              Select your dietary lifestyle choices to get personalized recipe suggestions
            </p>
      </div>
      
          <div className="grid grid-cols-2 gap-3">
            {dietaryOptions.map((diet) => {
              const isSelected = selectedDietary.includes(diet.value);
              return (
                <PreferenceCard
                  key={diet.value}
                  option={diet}
                  isSelected={isSelected}
                  onToggle={() => toggleDietary(diet.value)}
                />
              );
            })}
          </div>
        </motion.div>
        
        {/* Allergies Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                <span className="text-lg">⚠️</span>
              </div>
              <h2 className="text-xl font-heading font-bold text-gray-900 dark:text-gray-100">Allergies & Restrictions</h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm text-left">
              Help us keep you safe by marking any food allergies or intolerances
            </p>
        </div>
        
          <div className="grid grid-cols-2 gap-3">
            {allergyOptions.map((allergy) => {
              const isSelected = selectedAllergies.includes(allergy.value);
              return (
                <PreferenceCard
                  key={allergy.value}
                  option={allergy}
                  isSelected={isSelected}
                  onToggle={() => toggleAllergy(allergy.value)}
                />
              );
            })}
            
            {/* Custom allergies */}
            {customAllergies.map((customAllergy) => {
              const isSelected = selectedAllergies.includes(customAllergy);
              return (
                <CustomAllergyCard
                  key={customAllergy}
                  allergyName={customAllergy}
                  isSelected={isSelected}
                  onToggle={() => toggleAllergy(customAllergy)}
                  onRemove={() => onRemoveCustomAllergy(customAllergy)}
                />
              );
            })}
            
            {/* Add Custom Allergy Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleAddCustomAllergy}
              className="relative p-3 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-all duration-200 text-left bg-white dark:bg-gray-800 hover:shadow-sm"
            >
              <div className="flex items-center justify-center h-full">
                <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                  <span className="text-xl text-gray-500 dark:text-gray-400">+</span>
                </div>
              </div>
            </motion.button>
          </div>
        </motion.div>

        {/* Summary Card */}
        {(selectedDietary.length > 0 || selectedAllergies.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 mb-8"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                <span className="text-lg">✨</span>
        </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Your Preferences</h3>
            </div>
            
            <div className="space-y-3">
              {selectedDietary.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Dietary Preferences:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedDietary.map((diet) => {
                      const option = dietaryOptions.find(opt => opt.value === diet);
                      return (
                        <span 
                          key={diet} 
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-800/30 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-700/50"
                        >
                          <span className="mr-1">{option?.icon}</span>
                          {option?.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {selectedAllergies.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">Allergies & Restrictions:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedAllergies.map((allergy) => {
                      const option = allergyOptions.find(opt => opt.value === allergy);
                      // If it's a custom allergy (not found in allergyOptions), display it with warning icon
                      const displayName = option?.label || allergy;
                      const displayIcon = option?.icon || '⚠️';
                      
                      return (
                        <span 
                          key={allergy} 
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-800/30 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-700/50"
                        >
                          <span className="mr-1">{displayIcon}</span>
                          {displayName}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Save Button - REMOVED for auto-save functionality */}
      </div>
      
      {/* Add Custom Allergy Modal */}
      {showAddAllergyModal && ReactDOM.createPortal(
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[300]"
            onClick={handleCancelCustomAllergy}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[301] flex items-center justify-center p-4"
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Add Custom Allergy
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Enter the name of your custom allergy or food restriction.
                </p>
                
                <input 
                  type="text"
                  value={newAllergyName}
                  onChange={(e) => setNewAllergyName(e.target.value)}
                  placeholder="e.g., Sesame, Gluten, etc."
                  className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoFocus
                  onKeyPress={(e) => e.key === 'Enter' && handleSaveCustomAllergy()}
                />
                
                <div className="flex space-x-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={handleCancelCustomAllergy}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveCustomAllergy}
                    disabled={!newAllergyName.trim()}
                    className="flex-1"
                  >
                    Save
                  </Button>
          </div>
        </div>
      </div>
          </motion.div>
        </>,
        document.body
      )}
    </motion.div>
  );
};

const SettingsPage = ({ onBack, onLogout }: { onBack: () => void, onLogout: () => void }) => {
  const { user, updateUserMeasurementSystem, updateFloatingBarTheme, updateUserName } = useNoshStore();
  const { toast } = useToast();
  
  // Name editing state for mobile settings
  const [editableName, setEditableName] = useState(user.name);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Notification hook
  const {
    isLoading: notificationLoading,
    hasPermission,
    preferences,
    isSupported,
    toggleExpiryNotifications,
    toggleDailySummary,
    testNotification,
  } = useNotifications();
  
  // Sync editable name when user name changes
  useEffect(() => {
    setEditableName(user.name);
  }, [user.name]);
  
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditableName(e.target.value);
  };
  
  const handleNameBlur = async () => {
    if (editableName !== user.name && editableName.trim() !== '') {
      setIsUpdating(true);
      try {
        await updateUserName(editableName.trim());
        toast({
          title: "Name updated!",
          description: "Your name has been updated successfully.",
          duration: 1500,
        });
      } catch (error) {
        console.error('Error updating name:', error);
        toast({
          title: "Error",
          description: "Failed to update name. Please try again.",
          variant: "destructive",
        });
        setEditableName(user.name); // Revert to original name
      } finally {
        setIsUpdating(false);
      }
    }
  };
  
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur(); // Trigger the blur event to save
    }
  };
  
  // Utility functions for detecting actual time zone and language
  const getActualTimeZone = () => {
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const now = new Date();
      const timeString = now.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        timeZoneName: 'short'
      });
      return `${timeZone} (${timeString})`;
    } catch (error) {
      return 'Auto-detect';
    }
  };
  
  const getActualLanguage = () => {
    try {
      const language = navigator.language || navigator.languages?.[0] || 'en-US';
      const languageNames = new Intl.DisplayNames([language], { type: 'language' });
      const languageName = languageNames.of(language);
      return `${languageName} (${language})`;
    } catch (error) {
      return 'English (US)';
    }
  };

  // Disable mobile swipe back to prevent touch event conflicts with back button
  // The back button provides the navigation functionality
  // useMobileSwipeBack({
  //   enableSwipeBack: true,
  //   customBackHandler: () => {
  //     // Prevent any browser navigation - handle only internal navigation
  //     onBack();
  //     return false; // Explicitly prevent browser back
  //   },
  //   threshold: 80,
  //   edgeWidth: 60
  // });
  
  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.3 }}
      className="fixed inset-0 bg-gray-50 dark:bg-gray-900 z-50 overflow-y-auto"
      style={{
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain'
      }}
    >
      <div className="p-6 space-y-8 max-w-2xl mx-auto pb-40">
        {/* Header Section with Back Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-2">
              <button 
                onClick={onBack}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
              </button>
              <h2 className="text-xl font-heading font-bold text-gray-900 dark:text-gray-100">Settings</h2>
            </div>
          </div>
        </motion.div>

                 {/* Account Information Section */}
         <motion.div
           initial={{ opacity: 0, y: 20 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ delay: 0.2 }}
         >
           <div className="space-y-4">
             <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Account Information</h3>
             <div className="space-y-4">
               <div>
                 <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">Name</label>
                 <input
                   type="text"
                   value={editableName}
                   onChange={handleNameChange}
                   onBlur={handleNameBlur}
                   onKeyPress={handleKeyPress}
                   disabled={isUpdating}
                   className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:border-primary focus:ring-1 focus:ring-primary transition-colors disabled:opacity-50"
                   placeholder="Enter your name"
                 />
                 {isUpdating && (
                   <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Updating...</p>
                 )}
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">Email</label>
                 <input
                   type="email"
                   value={user.email}
                   className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                   readOnly
                 />
                 <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
               </div>
             </div>
           </div>
                   </motion.div>
        
        {/* Feedback Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <div className="space-y-4">
            <motion.div
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              className="transition-all duration-200"
            >
              <a
                href="https://forms.gle/i4TvhV6oXs7v8TJ77"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all duration-200"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
                    <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium">Send Feedback</p>
                    <p className="text-sm text-blue-600 dark:text-blue-500">Help us improve your experience</p>
                  </div>
                </div>
                                         <ExternalLink className="h-4 w-4 text-blue-500 dark:text-blue-400 ml-auto" />
              </a>
            </motion.div>
          </div>
        </motion.div>
        
        {/* General Settings Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">General Settings</h3>
          <div className="space-y-3">
            <div className="flex items-center p-4 bg-muted rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Globe className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Language</p>
                  <p className="text-sm text-muted-foreground">{getActualLanguage()}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center p-4 bg-muted rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Clock className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Time Zone</p>
                  <p className="text-sm text-muted-foreground">{getActualTimeZone()}</p>
                </div>
              </div>
            </div>
            
            <motion.div
              whileHover={{ scale: 1.01 }}
              className="flex items-center justify-between p-4 bg-muted rounded-lg transition-all duration-200"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Scale className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Measurement System</p>
                  <p className="text-sm text-muted-foreground">
                    {user.measurementSystem === 'metric' ? 'Metric (kg, g, l, ml)' : 'Imperial (lbs, oz, gal, fl oz)'}
                  </p>
                </div>
              </div>
              <div className="flex space-x-2">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => updateUserMeasurementSystem('metric')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border-2 transition-all duration-200 ${
                    user.measurementSystem === 'metric'
                      ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                      : 'bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-primary'
                  }`}
                >
                  Metric
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => updateUserMeasurementSystem('imperial')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border-2 transition-all duration-200 ${
                    user.measurementSystem === 'imperial'
                      ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                      : 'bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-primary'
                  }`}
                >
                  Imperial
                </motion.button>
              </div>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.01 }}
              className="flex items-center justify-between p-4 bg-muted rounded-lg transition-all duration-200"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Settings className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Floating Bar Theme</p>
                  <p className="text-sm text-muted-foreground">
                    {user.floatingBarTheme === 'dark' ? 'Dark Glass' : 'Light Glass'}
                  </p>
                </div>
              </div>
              <div className="flex space-x-2">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => updateFloatingBarTheme('light')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border-2 transition-all duration-200 ${
                    user.floatingBarTheme === 'light'
                      ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                      : 'bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-primary'
                  }`}
                >
                  Light
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => updateFloatingBarTheme('dark')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border-2 transition-all duration-200 ${
                    user.floatingBarTheme === 'dark'
                      ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                      : 'bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-primary'
                  }`}
                >
                  Dark
                </motion.button>
              </div>
            </motion.div>
            </div>
          </div>
        </motion.div>
        
        {/* Notification Settings Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Notification Settings</h3>
          <div className="space-y-3">
            <motion.div
              whileHover={{ scale: 1.01 }}
              className="flex items-center justify-between p-4 bg-muted rounded-lg transition-all duration-200"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Bell className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Expiry Notifications</p>
                  <p className="text-sm text-muted-foreground">
                    {isSupported 
                      ? "Get alerted when food is about to expire" 
                      : "Not supported in this browser"}
                  </p>
                  {!hasPermission && isSupported && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      Permission required
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {preferences && (
                  <button
                    onClick={testNotification}
                    disabled={notificationLoading}
                    className="px-2 py-1 text-xs bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors disabled:opacity-50"
                  >
                    Test
                  </button>
                )}
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={preferences?.expiry_notifications_enabled || false}
                    onChange={(e) => toggleExpiryNotifications(e.target.checked)}
                    disabled={!isSupported || notificationLoading}
                    className="sr-only peer" 
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary peer-disabled:opacity-50"></div>
                </label>
              </div>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.01 }}
              className="flex items-center justify-between p-4 bg-muted rounded-lg transition-all duration-200"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Star className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Daily Summary</p>
                  <p className="text-sm text-muted-foreground">
                    {isSupported 
                      ? "Daily overview of your food inventory" 
                      : "Not supported in this browser"}
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={preferences?.daily_summary_enabled || false}
                  onChange={(e) => toggleDailySummary(e.target.checked)}
                  disabled={!isSupported || notificationLoading}
                  className="sr-only peer" 
                />
                <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary peer-disabled:opacity-50"></div>
              </label>
            </motion.div>
            </div>
          </div>
        </motion.div>
        
        {/* Account Actions Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Account Actions</h3>
            <div className="space-y-3">
              <motion.div
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                className="transition-all duration-200"
              >
                <button
                  onClick={onLogout}
                  className="w-full flex items-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-all duration-200"
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg">
                      <LogOut className="h-5 w-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium">Logout</p>
                      <p className="text-sm text-red-600 dark:text-red-500">Sign out of your account</p>
                    </div>
                  </div>
                </button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

// Body Stats & Goals Page
const BodyStatsPage = ({ onBack }: { onBack: () => void }) => {
  const { user, updateBodyStats, calculateDailyCalorieTarget } = useNoshStore();
  const { toast } = useToast();
  
  // Get measurement system and labels
  const measurementSystem = user.measurementSystem || 'metric';
  const labels = getBodyStatsLabels(measurementSystem);
  
  // Local state for form inputs - always store in display units
  const [age, setAge] = useState(user.age?.toString() || '');
  const [gender, setGender] = useState<'male' | 'female' | 'other' | ''>(user.gender || '');
  
  // Height state for imperial (feet/inches) or metric (cm)
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');
  const [heightCm, setHeightCm] = useState('');
  
  // Weight state for imperial (lbs) or metric (kg)  
  const [weight, setWeight] = useState('');
  const [targetWeight, setTargetWeight] = useState('');
  
  const [goal, setGoal] = useState<'lose' | 'maintain' | 'gain' | ''>(user.goal || '');
  const [activityLevel, setActivityLevel] = useState<'sedentary' | 'light' | 'moderate' | 'active' | 'very_active' | ''>(user.activityLevel || '');
  
  const [isUpdating, setIsUpdating] = useState(false);

  // Initialize height and weight based on measurement system
  useEffect(() => {
    if (user.height) {
      if (measurementSystem === 'imperial') {
        const { feet, inches } = convertHeight.cmToFeetInches(user.height);
        setHeightFeet(feet.toString());
        setHeightInches(inches.toString());
      } else {
        setHeightCm(user.height.toString());
      }
    }
    
    if (user.weight) {
      if (measurementSystem === 'imperial') {
        setWeight(convertWeight.kgToLbs(user.weight).toString());
      } else {
        setWeight(user.weight.toString());
      }
    }
    
    if (user.targetWeight) {
      if (measurementSystem === 'imperial') {
        setTargetWeight(convertWeight.kgToLbs(user.targetWeight).toString());
      } else {
        setTargetWeight(user.targetWeight.toString());
      }
    }
  }, [user.height, user.weight, user.targetWeight, measurementSystem]);

  // Disable mobile swipe back to prevent touch event conflicts with back button
  // The back button provides the navigation functionality
  // useMobileSwipeBack({
  //   enableSwipeBack: true,
  //   customBackHandler: () => {
  //     // Prevent any browser navigation - handle only internal navigation
  //     onBack();
  //     return false; // Explicitly prevent browser back
  //   },
  //   threshold: 80,
  //   edgeWidth: 60
  // });

  // Options for dropdowns
  const genderOptions = [
    { value: 'male', label: 'Male', icon: '👨' },
    { value: 'female', label: 'Female', icon: '👩' },
    { value: 'other', label: 'Other', icon: '🧑' }
  ];

  const goalOptions = [
    { value: 'gain', label: 'Gain Muscle', icon: '📈', color: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/30', textColor: 'text-green-700 dark:text-green-300' },
    { value: 'maintain', label: 'Maintain Weight', icon: '⚖️', color: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/30', textColor: 'text-blue-700 dark:text-blue-300' },
    { value: 'lose', label: 'Lose Weight', icon: '📉', color: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/30', textColor: 'text-red-700 dark:text-red-300' }
  ];

  const activityOptions = [
    { value: 'sedentary', label: 'Sedentary', description: 'Little to no exercise', icon: '🛋️' },
    { value: 'light', label: 'Light Activity', description: 'Light exercise 1-3 days/week', icon: '🚶' },
    { value: 'moderate', label: 'Moderate Activity', description: 'Moderate exercise 3-5 days/week', icon: '🏃' },
    { value: 'active', label: 'Active', description: 'Hard exercise 6-7 days/week', icon: '🏋️' },
    { value: 'very_active', label: 'Very Active', description: 'Very hard exercise, physical job', icon: '🥇' }
  ];

  // Memoize nutrition targets calculation using SAVED user data (not live form data)
  // This prevents recalculation on every keystroke and only updates after save
  const nutritionTargets = useMemo(() => {
    // Use saved user data instead of live form data to prevent keystroke updates
    if (!user.age || !user.gender || !user.height || !user.weight || !user.activityLevel || !user.goal) {
      return null;
    }

    // Use enhanced nutrition calculator with dietary preferences
    return calculateNutritionTargets({
      age: user.age,
      gender: user.gender as 'male' | 'female' | 'other',
      height: user.height,
      weight: user.weight,
      goal: user.goal as 'lose' | 'maintain' | 'gain',
      activityLevel: user.activityLevel as 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active',
      dietaryPreferences: (user.preferences as DietaryPreference[]) || []
    });
  }, [user.age, user.gender, user.height, user.weight, user.goal, user.activityLevel, user.preferences]);

  const handleSave = async () => {
    setIsUpdating(true);
    try {
      const statsToUpdate: any = {};
      
      if (age) statsToUpdate.age = parseInt(age);
      if (gender) statsToUpdate.gender = gender;
      
      // Convert height to cm for database storage
      if (measurementSystem === 'imperial' && heightFeet && heightInches) {
        statsToUpdate.height = convertHeight.feetInchesToCm(parseInt(heightFeet), parseInt(heightInches));
      } else if (measurementSystem === 'metric' && heightCm) {
        statsToUpdate.height = parseFloat(heightCm);
      }
      
      // Convert weight to kg for database storage
      if (weight) {
        if (measurementSystem === 'imperial') {
          statsToUpdate.weight = convertWeight.lbsToKg(parseFloat(weight));
        } else {
          statsToUpdate.weight = parseFloat(weight);
        }
      }
              // Convert target weight to kg for database storage
        if (targetWeight) {
          if (measurementSystem === 'imperial') {
            statsToUpdate.targetWeight = convertWeight.lbsToKg(parseFloat(targetWeight));
          } else {
            statsToUpdate.targetWeight = parseFloat(targetWeight);
          }
        }
      if (goal) statsToUpdate.goal = goal;
      if (activityLevel) statsToUpdate.activityLevel = activityLevel;

      await updateBodyStats(statsToUpdate);
      
      toast({
        title: "Body stats updated!",
        description: "Your body stats and goals have been saved successfully.",
        duration: 1500,
      });
    } catch (error) {
      console.error('Error updating body stats:', error);
      toast({
        title: "Error",
        description: "Failed to update body stats. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const estimatedCalories = nutritionTargets?.dailyCalories;
  
  // Check if all required fields are filled based on measurement system
  const hasRequiredHeight = measurementSystem === 'imperial' ? (heightFeet && heightInches) : heightCm;
  const hasAllRequiredFields = age && gender && hasRequiredHeight && weight && activityLevel;

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.3 }}
      className="fixed inset-0 bg-gray-50 dark:bg-gray-900 z-50 overflow-y-auto"
      style={{
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain'
      }}
    >
      <div className="p-6 space-y-8 max-w-2xl mx-auto pb-40">
        {/* Header Section with Back Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-2">
              <button 
                onClick={onBack}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
              </button>
              <h2 className="text-xl font-heading font-bold text-gray-900 dark:text-gray-100">Body Stats & Goals</h2>
            </div>

          </div>
        </motion.div>

        {/* Daily Nutrition Targets - Shared Component */}
        <DailyNutritionTargets
          key="mobile-nutrition-targets"
          nutritionTargets={nutritionTargets}
          goal={goal || 'maintain'}
          isMobile={true}
          animationDelay={0.15}
        />

        {/* Body Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >

          {/* Basic Info Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Basic Information</h3>
            
            {/* Age and Gender Row */}
            <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Age</label>
              <input
                type="number"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                onBlur={async () => {
                  if (age) {
                    try {
                      await updateBodyStats({ age: parseInt(age) });
                      toast({
                        title: "Updated!",
                        description: "Age updated successfully.",
                        duration: 1500,
                      });
                    } catch (error) {
                      console.error('Error updating age:', error);
                      toast({
                        title: "Error",
                        description: "Failed to save age. Please try again.",
                        variant: "destructive",
                      });
                    }
                  }
                }}
                placeholder="25"
                className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gender</label>
              <select
                value={gender}
                onChange={async (e) => {
                  setGender(e.target.value as any);
                  if (e.target.value) {
                    try {
                      await updateBodyStats({ gender: e.target.value as any });
                      toast({
                        title: "Updated!",
                        description: "Gender updated successfully.",
                        duration: 1500,
                      });
                    } catch (error) {
                      console.error('Error updating gender:', error);
                      toast({
                        title: "Error",
                        description: "Failed to save gender. Please try again.",
                        variant: "destructive",
                      });
                    }
                  }
                }}
                className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">Select</option>
                {genderOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.icon} {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Height and Weight Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {labels.height.label} ({labels.height.unit})
              </label>
              {measurementSystem === 'imperial' ? (
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={heightFeet}
                    onChange={(e) => setHeightFeet(e.target.value)}
                    onBlur={async () => {
                      if (heightFeet && heightInches) {
                        try {
                          const heightInCm = convertHeight.feetInchesToCm(parseInt(heightFeet), parseInt(heightInches));
                          await updateBodyStats({ height: heightInCm });
                          toast({
                            title: "Updated!",
                            description: "Height updated successfully.",
                            duration: 1500,
                          });
                        } catch (error) {
                          console.error('Error updating height:', error);
                          toast({
                            title: "Error",
                            description: "Failed to save height. Please try again.",
                            variant: "destructive",
                          });
                        }
                      }
                    }}
                    placeholder="5"
                    className="flex-1 p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  <span className="flex items-center text-gray-500 dark:text-gray-400">ft</span>
                  <input
                    type="number"
                    value={heightInches}
                    onChange={(e) => setHeightInches(e.target.value)}
                    onBlur={async () => {
                      if (heightFeet && heightInches) {
                        try {
                          const heightInCm = convertHeight.feetInchesToCm(parseInt(heightFeet), parseInt(heightInches));
                          await updateBodyStats({ height: heightInCm });
                          toast({
                            title: "Updated!",
                            description: "Height updated successfully.",
                            duration: 1500,
                          });
                        } catch (error) {
                          console.error('Error updating height:', error);
                          toast({
                            title: "Error",
                            description: "Failed to save height. Please try again.",
                            variant: "destructive",
                          });
                        }
                      }
                    }}
                    placeholder="8"
                    className="flex-1 p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  <span className="flex items-center text-gray-500 dark:text-gray-400">in</span>
                </div>
              ) : (
                <input
                  type="number"
                  value={heightCm}
                  onChange={(e) => setHeightCm(e.target.value)}
                  onBlur={async () => {
                    if (heightCm) {
                      try {
                        await updateBodyStats({ height: parseFloat(heightCm) });
                        toast({
                          title: "Updated!",
                          description: "Height updated successfully.",
                          duration: 1500,
                        });
                      } catch (error) {
                        console.error('Error updating height:', error);
                        toast({
                          title: "Error",
                          description: "Failed to save height. Please try again.",
                          variant: "destructive",
                        });
                      }
                    }
                  }}
                  placeholder={labels.height.placeholder.split(' ')[1]}
                  className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {labels.weight.label} ({labels.weight.unit})
              </label>
              <input
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                onBlur={async () => {
                  if (weight) {
                    try {
                      const weightInKg = measurementSystem === 'imperial' 
                        ? convertWeight.lbsToKg(parseFloat(weight))
                        : parseFloat(weight);
                      await updateBodyStats({ weight: weightInKg });
                      toast({
                        title: "Updated!",
                        description: "Weight updated successfully.",
                        duration: 1500,
                      });
                    } catch (error) {
                      console.error('Error updating weight:', error);
                      toast({
                        title: "Error",
                        description: "Failed to save weight. Please try again.",
                        variant: "destructive",
                      });
                    }
                  }
                }}
                placeholder={labels.weight.placeholder.split(' ')[1]}
                className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
          </div>
        </motion.div>

        {/* Goals Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="space-y-4"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Your Goals</h3>
          
          <div className="grid grid-cols-3 gap-3">
            {goalOptions.map((option) => {
              const isSelected = goal === option.value;
              return (
                <motion.button
                  key={option.value}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={async () => {
                    setGoal(option.value as any);
                    try {
                      await updateBodyStats({ goal: option.value as any });
                      toast({
                        title: "Updated!",
                        description: "Goal updated successfully.",
                        duration: 1500,
                      });
                    } catch (error) {
                      console.error('Error updating goal:', error);
                      toast({
                        title: "Error",
                        description: "Failed to save goal. Please try again.",
                        variant: "destructive",
                      });
                    }
                  }}
                  className={`relative p-3 rounded-2xl border-2 transition-all duration-200 text-left w-full ${
                    isSelected
                      ? option.color + ' border-transparent shadow-md'
                      : 'bg-gray-100 dark:bg-gray-600 border-transparent shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-500'
                  }`}
                >
                  <div className="flex flex-col items-center space-y-2">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
                      isSelected ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-700'
                    }`}>
                      <span className="text-lg">{option.icon}</span>
                    </div>
                    
                    {/* Content */}
                    <div className="text-center">
                      <h3 className={`font-semibold text-xs transition-colors duration-200 ${
                        isSelected ? option.textColor : 'text-gray-900 dark:text-gray-100'
                      }`}>
                        {option.label}
                      </h3>
                    </div>
                    
                    {/* Checkbox indicator */}
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                      isSelected 
                        ? 'bg-white border-white shadow-sm' 
                        : 'border-gray-300 dark:border-gray-600'
                    }`}>
                      {isSelected && (
                        <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full" />
                      )}
                    </div>
                  </div>
                  
                  {/* Selection indicator background */}
                  {isSelected && (
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl pointer-events-none" />
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* Target Weight */}
          {goal && goal !== 'maintain' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Target {labels.weight.label} ({labels.weight.unit}) - Optional
              </label>
              <input
                type="number"
                value={targetWeight}
                onChange={(e) => setTargetWeight(e.target.value)}
                onBlur={async () => {
                  if (targetWeight) {
                    try {
                      const targetWeightInKg = measurementSystem === 'imperial' 
                        ? convertWeight.lbsToKg(parseFloat(targetWeight))
                        : parseFloat(targetWeight);
                      await updateBodyStats({ targetWeight: targetWeightInKg });
                      toast({
                        title: "Updated!",
                        description: "Target weight updated successfully.",
                        duration: 1500,
                      });
                    } catch (error) {
                      console.error('Error updating target weight:', error);
                      toast({
                        title: "Error",
                        description: "Failed to save target weight. Please try again.",
                        variant: "destructive",
                      });
                    }
                  }
                }}
                placeholder={measurementSystem === 'imperial' ? '140' : '65'}
                className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          )}
        </motion.div>

        {/* Activity Level Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-4"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Activity Level</h3>
          
          <div className="grid grid-cols-1 gap-3">
            {activityOptions.map((option) => {
              const isSelected = activityLevel === option.value;
              return (
                <motion.button
                  key={option.value}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={async () => {
                    setActivityLevel(option.value as any);
                    try {
                      await updateBodyStats({ activityLevel: option.value as any });
                      toast({
                        title: "Updated!",
                        description: "Activity level updated successfully.",
                        duration: 1500,
                      });
                    } catch (error) {
                      console.error('Error updating activity level:', error);
                      toast({
                        title: "Error",
                        description: "Failed to save activity level. Please try again.",
                        variant: "destructive",
                      });
                    }
                  }}
                  className={`relative p-3 rounded-2xl border-2 transition-all duration-200 text-left w-full ${
                    isSelected
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-transparent shadow-md'
                      : 'bg-gray-100 dark:bg-gray-600 border-transparent shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-500'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
                      isSelected ? 'bg-blue-100 dark:bg-blue-800/30' : 'bg-gray-200 dark:bg-gray-700'
                    }`}>
                      <span className="text-lg">{option.icon}</span>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1">
                      <h3 className={`font-semibold text-sm transition-colors duration-200 ${
                        isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-gray-100'
                      }`}>
                        {option.label}
                      </h3>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {option.description}
                      </div>
                    </div>
                    
                    {/* Checkbox indicator */}
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                      isSelected 
                        ? 'bg-white border-white shadow-sm' 
                        : 'border-gray-300 dark:border-gray-600'
                    }`}>
                      {isSelected && (
                        <div className="w-2.5 h-2.5 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full" />
                      )}
                    </div>
                  </div>
                  
                  {/* Selection indicator background */}
                  {isSelected && (
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl pointer-events-none" />
                  )}
                </motion.button>
              );
            })}
          </div>
        </motion.div>




      </div>
    </motion.div>
  );
};

const SubscriptionPage = ({ onBack }: { onBack: () => void }) => {
  // Disable mobile swipe back to prevent touch event conflicts with back button
  // The back button provides the navigation functionality
  // useMobileSwipeBack({
  //   enableSwipeBack: true,
  //   customBackHandler: () => {
  //     // Prevent any browser navigation - handle only internal navigation
  //     onBack();
  //     return false; // Explicitly prevent browser back
  //   },
  //   threshold: 80,
  //   edgeWidth: 60
  // });
  
  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.3 }}
      className="fixed inset-0 bg-white z-50 overflow-y-auto"
    >
      <div className="sticky top-0 bg-white border-b border-gray-200 p-4">
        <div className="flex items-center">
          <button 
            onClick={onBack}
            className="mr-4 p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-xl font-heading font-bold text-gray-900 dark:text-gray-100">Subscription</h2>
        </div>
      </div>
      
      <div className="p-6">
        {/* AI Usage Section */}
        <div className="mb-8">
          <AIUsageSectionMobile />
        </div>
        
        <div className="text-center mb-6">
          <Crown className="h-12 w-12 mx-auto text-yellow-500 mb-2" />
          <h3 className="text-xl font-semibold">Upgrade to Nosh Pro</h3>
          <p className="text-gray-600">Unlock premium features and get more out of Nosh</p>
        </div>
        
        <div className="space-y-4 mb-6">
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-medium text-yellow-800 mb-2">Pro Features</h4>
            <ul className="space-y-1 text-sm text-yellow-700">
              <li>• Full-featured inventory tracking</li>
              <li>• Unlimited photo/receipt scanning</li>
              <li>• AI recipe generation (200/month)</li>
              <li>• Unlimited meal logs</li>
              <li>• Full-featured meal planning</li>
              <li>• Personalized calorie tracker</li>
            </ul>
          </div>
        </div>
        
        <div className="space-y-3">
          <Button className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white">
            Upgrade to Pro - $15/month
          </Button>
        </div>
        
        <p className="text-xs text-gray-500 text-center mt-4">
          Cancel anytime. 7-day free trial for new subscribers.
        </p>
      </div>
    </motion.div>
  );
};

const AdminPage = ({ onBack }: { onBack: () => void }) => {
  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.3 }}
      className="fixed inset-0 bg-gray-50 dark:bg-gray-900 z-50 overflow-y-auto"
      style={{
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain'
      }}
    >
      <div className="p-6 space-y-8 max-w-2xl mx-auto pb-40">
        {/* Header Section with Back Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-2">
              <button 
                onClick={onBack}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
              </button>
              <h2 className="text-xl font-heading font-bold text-gray-900 dark:text-gray-100">Admin Panel</h2>
            </div>
          </div>
        </motion.div>

        {/* Hackathon Mode Section - Admin Only */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">🎯 Hackathon Mode</h3>
            <CaptchaToggle isMobile={true} />
          </div>
        </motion.div>

        {/* Admin AI Usage Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <AdminAIUsagePanel className="w-full" />
        </motion.div>
      </div>
    </motion.div>
  );
};

const Profile = () => {
  const { user, updateUserAvatar, updateUserApiProvider, updateUserName, loadUserProfile, calculateDailyCalorieTarget } = useNoshStore();
  const [searchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState('bodystats');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [currentPage, setCurrentPage] = useState<string | null>(null);

  // Handle tab URL parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['bodystats', 'preferences', 'settings', 'subscription', 'admin'].includes(tabParam)) {
      setSelectedTab(tabParam);
    }
  }, [searchParams]);
  const [isAvatarSelectorOpen, setIsAvatarSelectorOpen] = useState(false);
  const [isAnnouncementEditorOpen, setIsAnnouncementEditorOpen] = useState(false);
  const [currentAnnouncementConfig, setCurrentAnnouncementConfig] = useState(announcementConfigManager.getCurrentConfig());
  const [isMobileAvatarSelectorOpen, setIsMobileAvatarSelectorOpen] = useState(false);
  const [mobileDefaultAvatars, setMobileDefaultAvatars] = useState<any[]>([]);
  const [selectedMobileAvatar, setSelectedMobileAvatar] = useState(user.avatar);
  const [mobileAvatarLoading, setMobileAvatarLoading] = useState(false);
  const [mobileAvatarSubmitting, setMobileAvatarSubmitting] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Desktop preferences state (same logic as mobile)
  const [desktopDietaryPrefs, setDesktopDietaryPrefs] = useState<string[]>(user.preferences || []);
  const [desktopAllergies, setDesktopAllergies] = useState<string[]>(user.allergies || []);
  
  // Desktop name editing state
  const [desktopEditableName, setDesktopEditableName] = useState(user.name);
  const [isDesktopNameUpdating, setIsDesktopNameUpdating] = useState(false);
  
  // Custom allergies modal state for desktop
  const [showDesktopAddAllergyModal, setShowDesktopAddAllergyModal] = useState(false);
  const [desktopNewAllergyName, setDesktopNewAllergyName] = useState('');
  
  // Shared custom allergies state (used by both mobile and desktop)
  const [customAllergies, setCustomAllergies] = useState<string[]>([]);
  
  // Desktop tooltip state for dietary preferences
  const [showDietaryTooltip, setShowDietaryTooltip] = useState(false);
  const [dietaryTooltipData, setDietaryTooltipData] = useState<{diet: any, buttonRect: DOMRect} | null>(null);
  
  const { toast } = useToast();

  // Notification hook for desktop profile
  const {
    isLoading: notificationLoading,
    hasPermission,
    preferences,
    isSupported,
    toggleExpiryNotifications,
    toggleDailySummary,
    testNotification,
  } = useNotifications();

  // Check if app is running in standalone mode (PWA)
  useEffect(() => {
    const checkStandalone = () => {
      const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches ||
                              (window.navigator as any).standalone ||
                              document.referrer.includes('android-app://');
      setIsStandalone(isStandaloneMode);
    };

    checkStandalone();
    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addListener(checkStandalone);
    
    return () => mediaQuery.removeListener(checkStandalone);
  }, []);

  // Subscribe to announcement configuration changes
  useEffect(() => {
    const unsubscribe = announcementConfigManager.subscribe((config) => {
      setCurrentAnnouncementConfig(config);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Load user profile on mount
  useEffect(() => {
    loadUserProfile();
  }, []); // Empty dependency array to run only once

  // Update desktop state when user data changes
  useEffect(() => {
    setDesktopDietaryPrefs(user.preferences || []);
    setDesktopAllergies(user.allergies || []);
    
    // Extract custom allergies from user.allergies (ones not in predefined allergyOptions)
    const predefinedAllergyValues = allergyOptions.map(option => option.value);
    const userCustomAllergies = (user.allergies || []).filter(allergy => 
      !predefinedAllergyValues.includes(allergy)
    );
    setCustomAllergies(userCustomAllergies);
  }, [user.preferences, user.allergies]);

  // Disable left edge swipe prevention completely for Profile page
  // The subpages have their own useMobileSwipeBack functionality that conflicts with useLeftEdgeSwipeDisable
  // This was causing back buttons to be unresponsive and require multiple taps
  // useLeftEdgeSwipeDisable({
  //   pageId: 'Profile',
  //   enabled: !currentPage, // Only enable on main page (when currentPage is null/undefined), not sub-pages
  //   edgeWidth: 50
  // });

  // Toggle functions for desktop (same logic as mobile)
  const toggleDesktopDietary = async (value: string) => {
    const newSelection = desktopDietaryPrefs.includes(value) ? [] : [value];
    setDesktopDietaryPrefs(newSelection);
    
    try {
      // Auto-save to database immediately
      await useNoshStore.getState().updateUserPreferences(newSelection);
      toast({
        title: "Preferences updated!",
        description: `Dietary preference ${newSelection.length > 0 ? 'set to ' + value : 'cleared'}.`,
        duration: 1500, // 1.5 seconds - quick feedback
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save preferences. Please try again.",
        variant: "destructive",
        duration: 1500, // 1.5 seconds
      });
      // Revert on error
      setDesktopDietaryPrefs(desktopDietaryPrefs);
    }
  };

  const toggleDesktopAllergy = async (value: string) => {
    const newSelection = desktopAllergies.includes(value) 
      ? desktopAllergies.filter(item => item !== value)
      : [...desktopAllergies, value];
    setDesktopAllergies(newSelection);
    
    try {
      // Auto-save to database immediately
      await useNoshStore.getState().updateUserAllergies(newSelection);
      toast({
        title: "Allergies updated!",
        description: `${desktopAllergies.includes(value) ? 'Removed' : 'Added'} ${value} allergy.`,
        duration: 1500, // 1.5 seconds - quick feedback
      });
      // Ensure nav stays hidden after toast
    } catch (error) {
      toast({
        title: "Error", 
        description: "Failed to save allergies. Please try again.",
        variant: "destructive",
        duration: 1500, // 1.5 seconds
      });
      // Revert on error
      setDesktopAllergies(user.allergies || []);
    }
  };

  // Desktop name handling functions
  const handleDesktopNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDesktopEditableName(e.target.value);
  };
  
  const handleDesktopNameBlur = async () => {
    if (desktopEditableName !== user.name && desktopEditableName.trim() !== '') {
      setIsDesktopNameUpdating(true);
      try {
        await updateUserName(desktopEditableName.trim());
        toast({
          title: "Name updated!",
          description: "Your name has been updated successfully.",
          duration: 1500,
        });
      } catch (error) {
        console.error('Error updating name:', error);
        toast({
          title: "Error",
          description: "Failed to update name. Please try again.",
          variant: "destructive",
        });
        setDesktopEditableName(user.name); // Revert to original name
      } finally {
        setIsDesktopNameUpdating(false);
      }
    }
  };
  
  const handleDesktopNameKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur(); // Trigger the blur event to save
    }
  };

  // Desktop tooltip handlers for dietary preferences
  const handleDietaryMouseEnter = (diet: any, event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isMobile && diet.tooltip) {
      const buttonRect = event.currentTarget.getBoundingClientRect();
      setDietaryTooltipData({ diet, buttonRect });
      setShowDietaryTooltip(true);
    }
  };

  const handleDietaryMouseLeave = () => {
    setShowDietaryTooltip(false);
    setDietaryTooltipData(null);
  };

  // Handle adding custom allergy for desktop
  const handleDesktopAddCustomAllergy = () => {
    setShowDesktopAddAllergyModal(true);
  };

  const handleDesktopSaveCustomAllergy = async () => {
    if (!desktopNewAllergyName.trim()) return;
    
    const trimmedName = desktopNewAllergyName.trim();
    const newCustomList = [...customAllergies, trimmedName];
    const updatedAllergies = [...desktopAllergies, trimmedName]; // Auto-select the new allergy
    
    setCustomAllergies(newCustomList);
    setDesktopAllergies(updatedAllergies);
    setDesktopNewAllergyName('');
    setShowDesktopAddAllergyModal(false);
    
    try {
      // Auto-save to database immediately
      await useNoshStore.getState().updateUserAllergies(updatedAllergies);
      toast({
        title: "Custom allergy added!",
        description: `Added ${trimmedName} to your allergies.`,
        duration: 1500,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save custom allergy. Please try again.",
        variant: "destructive",
        duration: 1500,
      });
      // Revert on error
      setCustomAllergies(customAllergies);
      setDesktopAllergies(desktopAllergies);
    }
  };

  const handleDesktopCancelCustomAllergy = () => {
    setDesktopNewAllergyName('');
    setShowDesktopAddAllergyModal(false);
  };

  // Handle removing custom allergy (shared by both mobile and desktop)
  const handleRemoveCustomAllergy = async (allergyToRemove: string) => {
    const newCustomList = customAllergies.filter(allergy => allergy !== allergyToRemove);
    const updatedAllergies = [...desktopAllergies.filter(allergy => allergy !== allergyToRemove)];
    
    setCustomAllergies(newCustomList);
    setDesktopAllergies(updatedAllergies);
    
    try {
      // Auto-save to database immediately
      await useNoshStore.getState().updateUserAllergies(updatedAllergies);
      toast({
        title: "Custom allergy removed!",
        description: `Removed ${allergyToRemove} from your allergies.`,
        duration: 1500,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove custom allergy. Please try again.",
        variant: "destructive",
        duration: 1500,
      });
      // Revert on error
      setCustomAllergies(customAllergies);
      setDesktopAllergies(desktopAllergies);
    }
  };

  // Desktop Body Stats Form Component
  const DesktopBodyStatsForm = () => {
    // Get measurement system and labels
    const measurementSystem = user.measurementSystem || 'metric';
    const labels = getBodyStatsLabels(measurementSystem);
    
    const [formData, setFormData] = useState({
      age: user.age?.toString() || '',
      gender: user.gender || '',
      heightFeet: '',
      heightInches: '',
      heightCm: '',
      weight: '',
      targetWeight: '',
      goal: user.goal || '',
      activityLevel: user.activityLevel || ''
    });

    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    
    // Initialize height and weight based on measurement system
    useEffect(() => {
      if (user.height !== undefined) {
        if (measurementSystem === 'imperial') {
          const { feet, inches } = convertHeight.cmToFeetInches(user.height!);
          setFormData(prev => ({
            ...prev,
            heightFeet: feet.toString(),
            heightInches: inches.toString()
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            heightCm: user.height!.toString()
          }));
        }
      }
      
      if (user.weight !== undefined) {
        if (measurementSystem === 'imperial') {
          setFormData(prev => ({
            ...prev,
            weight: convertWeight.kgToLbs(user.weight!).toString()
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            weight: user.weight!.toString()
          }));
        }
      }
      
      if (user.targetWeight !== undefined) {
        if (measurementSystem === 'imperial') {
          setFormData(prev => ({
            ...prev,
            targetWeight: convertWeight.kgToLbs(user.targetWeight!).toString()
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            targetWeight: user.targetWeight!.toString()
          }));
        }
      }
    }, [user.height, user.weight, user.targetWeight, measurementSystem]);

    const handleInputChange = (field: string, value: string) => {
      setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleAutoSave = async (field: string, value: string) => {
      if (!value) return;
      
      // Check if value actually changed to prevent unnecessary saves
      const currentValue = getCurrentFieldValue(field);
      if (value === currentValue) {
        return; // No change, don't save
      }
      
      setIsLoading(true);
      try {
        const bodyStats: any = {};
        
        if (field === 'age') bodyStats.age = parseInt(value);
        if (field === 'gender') bodyStats.gender = value;
        
        // Handle height conversion
        if (field === 'heightCm' || field === 'heightFeet' || field === 'heightInches') {
          if (measurementSystem === 'imperial' && formData.heightFeet && formData.heightInches) {
            bodyStats.height = convertHeight.feetInchesToCm(parseInt(formData.heightFeet), parseInt(formData.heightInches));
          } else if (measurementSystem === 'metric' && formData.heightCm) {
            bodyStats.height = parseFloat(formData.heightCm);
          }
        }
        
        // Handle weight conversion
        if (field === 'weight' && value) {
          if (measurementSystem === 'imperial') {
            bodyStats.weight = convertWeight.lbsToKg(parseFloat(value));
          } else {
            bodyStats.weight = parseFloat(value);
          }
        }
        
        // Handle target weight conversion
        if (field === 'targetWeight' && value) {
          if (measurementSystem === 'imperial') {
            bodyStats.targetWeight = convertWeight.lbsToKg(parseFloat(value));
          } else {
            bodyStats.targetWeight = parseFloat(value);
          }
        }
        
        if (field === 'goal') bodyStats.goal = value;
        if (field === 'activityLevel') bodyStats.activityLevel = value;
        
        await useNoshStore.getState().updateBodyStats(bodyStats);
      } catch (error) {
        console.error('Failed to update body stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Helper function to get current field value from user data
    const getCurrentFieldValue = (field: string): string => {
      switch (field) {
        case 'age':
          return user.age?.toString() || '';
        case 'gender':
          return user.gender || '';
        case 'heightCm':
          return measurementSystem === 'metric' ? (user.height?.toString() || '') : '';
        case 'heightFeet':
          if (measurementSystem === 'imperial' && user.height) {
            const { feet } = convertHeight.cmToFeetInches(user.height);
            return feet.toString();
          }
          return '';
        case 'heightInches':
          if (measurementSystem === 'imperial' && user.height) {
            const { inches } = convertHeight.cmToFeetInches(user.height);
            return inches.toString();
          }
          return '';
        case 'weight':
          if (user.weight) {
            return measurementSystem === 'imperial' 
              ? convertWeight.kgToLbs(user.weight).toString()
              : user.weight.toString();
          }
          return '';
        case 'targetWeight':
          if (user.targetWeight) {
            return measurementSystem === 'imperial'
              ? convertWeight.kgToLbs(user.targetWeight).toString()
              : user.targetWeight.toString();
          }
          return '';
        case 'goal':
          return user.goal || '';
        case 'activityLevel':
          return user.activityLevel || '';
        default:
          return '';
      }
    };

    // Memoize nutrition targets calculation using SAVED user data (not live form data)
    // This prevents recalculation on every keystroke and only updates after save
    const nutritionTargets = useMemo(() => {
      // Use saved user data instead of live form data to prevent keystroke updates
      if (!user.age || !user.gender || !user.height || !user.weight || !user.activityLevel || !user.goal) {
        return null;
      }

      // Use enhanced nutrition calculator with dietary preferences
      return calculateNutritionTargets({
        age: user.age,
        gender: user.gender as 'male' | 'female' | 'other',
        height: user.height,
        weight: user.weight,
        goal: user.goal as 'lose' | 'maintain' | 'gain',
        activityLevel: user.activityLevel as 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active',
        dietaryPreferences: (desktopDietaryPrefs as DietaryPreference[]) || []
      });
    }, [
      user.age, 
      user.gender, 
      user.height, 
      user.weight, 
      user.goal, 
      user.activityLevel, 
      desktopDietaryPrefs
    ]);
    const dailyCalories = nutritionTargets?.dailyCalories;

    return (
      <div>


        {/* Daily Nutrition Targets - Shared Component */}
        <DailyNutritionTargets
          key="desktop-nutrition-targets"
          nutritionTargets={nutritionTargets}
          goal={formData.goal || 'maintain'}
          isMobile={false}
        />

        {successMessage && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
            <p className="text-green-800 dark:text-green-200 text-center">{successMessage}</p>
          </div>
        )}

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Age */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Age
              </label>
              <input
                type="number"
                value={formData.age}
                onChange={(e) => handleInputChange('age', e.target.value)}
                onBlur={(e) => handleAutoSave('age', e.target.value)}
                className="w-full px-4 py-3 bg-input border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Enter your age"
                min="1"
                max="120"
              />
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Gender
              </label>
              <select
                value={formData.gender}
                onChange={(e) => {
                  handleInputChange('gender', e.target.value);
                  handleAutoSave('gender', e.target.value);
                }}
                className="w-full px-4 py-3 bg-input border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Height */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                {labels.height.label} ({labels.height.unit})
              </label>
              {measurementSystem === 'imperial' ? (
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={formData.heightFeet}
                    onChange={(e) => handleInputChange('heightFeet', e.target.value)}
                    onBlur={(e) => handleAutoSave('heightFeet', e.target.value)}
                    className="flex-1 px-4 py-3 bg-input border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="5"
                    min="1"
                    max="9"
                  />
                  <span className="flex items-center text-text-secondary">ft</span>
                  <input
                    type="number"
                    value={formData.heightInches}
                    onChange={(e) => handleInputChange('heightInches', e.target.value)}
                    onBlur={(e) => handleAutoSave('heightInches', e.target.value)}
                    className="flex-1 px-4 py-3 bg-input border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="8"
                    min="0"
                    max="11"
                  />
                  <span className="flex items-center text-text-secondary">in</span>
                </div>
              ) : (
                <input
                  type="number"
                  value={formData.heightCm}
                  onChange={(e) => handleInputChange('heightCm', e.target.value)}
                  onBlur={(e) => handleAutoSave('heightCm', e.target.value)}
                  className="w-full px-4 py-3 bg-input border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="170"
                  min="50"
                  max="300"
                  step="0.1"
                />
              )}
            </div>

            {/* Weight */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Current {labels.weight.label} ({labels.weight.unit})
              </label>
              <input
                type="number"
                value={formData.weight}
                onChange={(e) => handleInputChange('weight', e.target.value)}
                onBlur={(e) => handleAutoSave('weight', e.target.value)}
                className="w-full px-4 py-3 bg-input border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder={measurementSystem === 'imperial' ? '150' : '70'}
                min="20"
                max="500"
                step="0.1"
              />
            </div>
          </div>

          {/* Goal */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Your Goal
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'gain', label: 'Gain Muscle', emoji: '📈', color: 'text-green-600' },
                { value: 'maintain', label: 'Maintain Weight', emoji: '⚖️', color: 'text-blue-600' },
                { value: 'lose', label: 'Lose Weight', emoji: '📉', color: 'text-red-600' }
              ].map((goal) => (
                <button
                  key={goal.value}
                  type="button"
                  onClick={() => {
                    handleInputChange('goal', goal.value);
                    handleAutoSave('goal', goal.value);
                  }}
                  className={`p-4 border rounded-lg text-center transition-all ${
                    formData.goal === goal.value
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="text-2xl mb-2">{goal.emoji}</div>
                  <div className="font-medium">{goal.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Target Weight */}
          {formData.goal && formData.goal !== 'maintain' && (
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Target {labels.weight.label} ({labels.weight.unit})
              </label>
              <input
                type="number"
                value={formData.targetWeight}
                onChange={(e) => handleInputChange('targetWeight', e.target.value)}
                onBlur={(e) => handleAutoSave('targetWeight', e.target.value)}
                className="w-full px-4 py-3 bg-input border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Enter your target weight"
                min="20"
                max="500"
                step="0.1"
              />
            </div>
          )}

          {/* Activity Level */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Activity Level
            </label>
            <select
              value={formData.activityLevel}
              onChange={(e) => {
                handleInputChange('activityLevel', e.target.value);
                handleAutoSave('activityLevel', e.target.value);
              }}
              className="w-full px-4 py-3 bg-input border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Select activity level</option>
              <option value="sedentary">Sedentary (little/no exercise)</option>
              <option value="light">Light Activity (light exercise 1-3 days/week)</option>
              <option value="moderate">Moderate Activity (moderate exercise 3-5 days/week)</option>
              <option value="active">Active (hard exercise 6-7 days/week)</option>
              <option value="very_active">Very Active (very hard exercise, physical job)</option>
            </select>
          </div>



        </div>
      </div>
    );
  };

  // Load mobile avatars when selector opens
  useEffect(() => {
    if (isMobileAvatarSelectorOpen && mobileDefaultAvatars.length === 0) {
      loadMobileAvatars();
    }
  }, [isMobileAvatarSelectorOpen]);

  // Sync editable name states when user name changes
  useEffect(() => {
    setDesktopEditableName(user.name);
  }, [user.name]);

  // Cleanup: Restore mobile navigation if component unmounts while modal is open
  useEffect(() => {
    return () => {
      // Restore mobile navigation on cleanup
      const mobileNav = document.querySelector('.mobile-navigation');
      if (mobileNav) {
        (mobileNav as HTMLElement).style.transform = 'translateY(0)';
      }
    };
  }, []);

  // Restore mobile navigation when modal closes
  useEffect(() => {
    if (!isMobileAvatarSelectorOpen) {
      const mobileNav = document.querySelector('.mobile-navigation');
      if (mobileNav) {
        setTimeout(() => {
          (mobileNav as HTMLElement).style.transform = 'translateY(0)';
          (mobileNav as HTMLElement).style.transition = 'transform 0.3s ease-in-out';
        }, 100); // Small delay to ensure smooth transition
      }
    }
  }, [isMobileAvatarSelectorOpen]);

  const loadMobileAvatars = async () => {
    setMobileAvatarLoading(true);
    try {
      const avatars = await db.getDefaultAvatars();
      setMobileDefaultAvatars(avatars);
      setSelectedMobileAvatar(user.avatar);
    } catch (error) {
      console.error('Error loading mobile avatars:', error);
      toast({
        title: "Error",
        description: "Failed to load avatar options. Please try again.",
        variant: "destructive",
      });
    } finally {
      setMobileAvatarLoading(false);
    }
  };

  const handleMobileAvatarSelect = (avatarUrl: string) => {
    setSelectedMobileAvatar(avatarUrl);
  };

  const handleMobileAvatarConfirm = async () => {
    if (!selectedMobileAvatar || selectedMobileAvatar === user.avatar) return;

    setMobileAvatarSubmitting(true);
    try {
      await updateUserAvatar(selectedMobileAvatar);
      handleCloseMobileAvatarSelector();
      toast({
        title: "Profile updated!",
        description: "Your avatar has been updated successfully.",
      });
    } catch (error) {
      console.error('Error updating mobile avatar:', error);
      toast({
        title: "Error",
        description: "Failed to update avatar. Please try again.",
        variant: "destructive",
      });
    } finally {
      setMobileAvatarSubmitting(false);
    }
  };

  // Handle opening mobile avatar selector
  const handleOpenMobileAvatarSelector = () => {
    setIsMobileAvatarSelectorOpen(true);
    // Hide mobile navigation
    const mobileNav = document.querySelector('.mobile-navigation');
    if (mobileNav) {
      (mobileNav as HTMLElement).style.transform = 'translateY(100%)';
      (mobileNav as HTMLElement).style.transition = 'transform 0.3s ease-in-out';
    }
  };

  // Handle closing mobile avatar selector
  const handleCloseMobileAvatarSelector = () => {
    setIsMobileAvatarSelectorOpen(false);
    // Show mobile navigation
    const mobileNav = document.querySelector('.mobile-navigation');
    if (mobileNav) {
      (mobileNav as HTMLElement).style.transform = 'translateY(0)';
      (mobileNav as HTMLElement).style.transition = 'transform 0.3s ease-in-out';
    }
  };

  const handleAvatarUpdate = async (newAvatarUrl: string) => {
    try {
      await updateUserAvatar(newAvatarUrl);
      toast({
        title: "Profile updated!",
        description: "Your avatar has been updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update avatar. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle logout function
  const handleLogout = async () => {
    try {
      const { handleLogoutRedirect } = await import('../lib/logout-utils');
      await handleLogoutRedirect();
    } catch (error) {
      console.error('Error logging out:', error);
      // Fallback: redirect using utility
      const { getLogoutRedirectUrl } = await import('../lib/logout-utils');
      window.location.href = getLogoutRedirectUrl();
    }
  };

  // Hard refresh function that clears cache but preserves authentication
  const handleHardRefresh = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    
    try {
      // Clear application cache but preserve authentication
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }

      // Clear all caches except authentication-related ones
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        const authCacheNames = cacheNames.filter(name => 
          name.includes('auth') || 
          name.includes('session') || 
          name.includes('supabase')
        );
        
        // Delete non-auth caches
        const deletePromises = cacheNames
          .filter(name => !authCacheNames.includes(name))
          .map(name => caches.delete(name));
        
        await Promise.all(deletePromises);
      }

      toast({
        title: "Refreshing app...",
        description: "Clearing cache and reloading the latest version.",
      });

      // Force reload with cache bypass after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error('Error during hard refresh:', error);
      setIsRefreshing(false);
      toast({
        title: "Refresh failed",
        description: "Unable to clear cache. Try closing and reopening the app.",
        variant: "destructive",
      });
    }
  };

  // Announcement management functions
  const handleSaveAnnouncement = async (updatedAnnouncement: Announcement) => {
    try {
      const result = await announcementConfigManager.updateConfig(updatedAnnouncement);
      
      if (result.success) {
        toast({
          title: "Announcement updated",
          description: "The announcement has been saved and is now live for all users.",
        });
      } else {
        toast({
          title: "Failed to update announcement",
          description: result.error || "An unknown error occurred.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error saving announcement:', error);
      toast({
        title: "Error",
        description: "Failed to save announcement. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handlePreviewAnnouncement = () => {
    // For now, just open the editor in preview mode
    setIsAnnouncementEditorOpen(true);
  };

  const menuItems = [
    {
      id: 'bodystats',
      name: 'Body Stats & Goals',
      icon: <Scale size={20} />,
      description: 'Set your body stats and nutrition goals'
    },
    {
      id: 'preferences',
      name: 'Preferences',
      icon: <Heart size={20} />,
      description: 'Dietary preferences and allergies'
    },
    {
      id: 'settings',
      name: 'Settings',
      icon: <Settings size={20} />,
      description: 'Account information and app settings'
    },
    {
      id: 'subscription',
      name: 'Subscription',
      icon: <Crown size={20} />,
      description: 'Manage your plan and billing'
    },
    // Admin-only menu item
    ...(user.isAdmin ? [{
      id: 'admin',
      name: 'Admin Panel',
      icon: <Target size={20} />,
      description: 'AI usage analytics and system controls'
    }] : [])
  ];

  const handleMenuClick = (pageId: string) => {
    if (isMobile) {
      setCurrentPage(pageId);
    } else {
      // Desktop: use tab functionality
      setSelectedTab(pageId);
    }
  };

  const handleBackClick = () => {
    // Ensure mobile navigation is restored when returning from sub-pages
    setTimeout(() => {
      const mobileNav = document.querySelector('.mobile-navigation');
      if (mobileNav) {
        (mobileNav as HTMLElement).style.transform = 'translateY(0)';
        (mobileNav as HTMLElement).style.transition = 'transform 0.3s ease-in-out';
      }
    }, 100); // Delay to ensure the page transition has started
    
    setCurrentPage(null);
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'bodystats':
        return <BodyStatsPage onBack={handleBackClick} />;
      case 'preferences':
        return (
          <PreferencesPage 
            onBack={handleBackClick}
            customAllergies={customAllergies}
            setCustomAllergies={setCustomAllergies}
            onRemoveCustomAllergy={handleRemoveCustomAllergy}
          />
        );
      case 'settings':
        return <SettingsPage onBack={handleBackClick} onLogout={handleLogout} />;
      case 'subscription':
        return <SubscriptionPage onBack={handleBackClick} />;
      case 'admin':
        return <AdminPage onBack={handleBackClick} />;
      default:
        return null;
    }
  };

  const createMobileAvatarPortal = () => {
    if (isMobileAvatarSelectorOpen) {
      return ReactDOM.createPortal(
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[200]"
            onClick={handleCloseMobileAvatarSelector}
          />
          
          {/* Mobile Avatar Selector Sheet */}
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl z-[201] max-h-[85vh] overflow-hidden"
          >
            {/* Handle */}
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mt-3 mb-3"></div>
            
            {/* Header */}
            <div className="px-4 pb-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Choose Profile Picture
                </h2>
                <button 
                  onClick={handleCloseMobileAvatarSelector}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Select one of our default avatars for your profile
              </p>
            </div>

            {/* Avatar Grid */}
            <div className="px-3 py-3 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 160px)' }}>
              {mobileAvatarLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {mobileDefaultAvatars.map((avatar) => (
                    <button
                      key={avatar.id}
                      onClick={() => handleMobileAvatarSelect(avatar.url)}
                      className={`relative aspect-square bg-gray-100 rounded-lg border-2 transition-colors flex items-center justify-center ${
                        selectedMobileAvatar === avatar.url
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-primary/50'
                      }`}
                    >
                      <img 
                        src={avatar.url} 
                        alt={avatar.name}
                        className="w-16 h-16 object-contain"
                        loading="lazy"
                      />
                      
                      {selectedMobileAvatar === avatar.url && (
                        <div className="absolute top-1 right-1 bg-primary text-white rounded-full p-1">
                          <BadgeCheck size={14} />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={handleCloseMobileAvatarSelector}
                  disabled={mobileAvatarSubmitting}
                  className="px-4 py-2 text-base"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleMobileAvatarConfirm}
                  disabled={!selectedMobileAvatar || mobileAvatarSubmitting || selectedMobileAvatar === user.avatar}
                  className="px-4 py-2 text-base"
                >
                  {mobileAvatarSubmitting ? (
                    <div className="flex items-center space-x-1">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      <span>Updating...</span>
                    </div>
                  ) : (
                    'Update Avatar'
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </>,
        document.body
      );
    }
    return null;
  };

  // Handle mobile navigation visibility based on current page
  useEffect(() => {
    if (isMobile) {
      if (currentPage) {
        // In a sub-page - hide navigation
        document.body.classList.add('preferences-page');
      } else {
        // On main profile page - show navigation
        document.body.classList.remove('preferences-page');
      }
    }

    return () => {
      // Clean up when component unmounts
      document.body.classList.remove('preferences-page');
    };
  }, [currentPage, isMobile]);



  return (
    <>
      {isMobile ? (
        <>
          <div className="h-screen -mx-4 -mt-6 bg-gray-100 grid grid-rows-[auto_1fr_auto] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
            {/* Hard Refresh Button - PWA Standalone Only - Fixed position */}
            {isStandalone && isMobile && (
              <motion.button
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
                onClick={handleHardRefresh}
                disabled={isRefreshing}
                className="fixed top-3 right-3 z-30 bg-black/20 backdrop-blur-sm text-black rounded-full p-2 hover:bg-black/30 transition-all duration-200 flex items-center justify-center shadow-lg border border-black/30 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Hard refresh - Clear cache and reload"
              >
                <motion.div
                  animate={{ rotate: isRefreshing ? 360 : 0 }}
                  transition={{ 
                    duration: isRefreshing ? 1 : 0, 
                    repeat: isRefreshing ? Infinity : 0,
                    ease: "linear" 
                  }}
                >
                  <RotateCcw size={16} />
                </motion.div>
              </motion.button>
            )}

            {/* Minimal top spacer */}
            <div className="pt-5"></div>

            {/* Flexible center section - Profile dynamically centered */}
            <div className="flex items-center justify-center px-4">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                {/* Avatar Section */}
                <div className="relative inline-block mb-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                    className="h-48 w-48 flex items-center justify-center rounded-xl"
                  >
                    <img 
                      src={user.avatar} 
                      alt={user.name}
                      className="h-40 w-40 object-contain"
                      loading="lazy"
                    />
                  </motion.div>
                  <button 
                    className="absolute -bottom-2 -right-2 bg-white text-primary rounded-full p-2 hover:bg-gray-100 transition-colors flex items-center justify-center shadow-lg border-2 border-primary"
                    onClick={handleOpenMobileAvatarSelector}
                  >
                    <Camera size={20} />
                  </button>
                </div>
                
                {/* User Info */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-gray-800"
                >
                  <h2 className="text-2xl font-heading font-bold mb-2">{user.name}</h2>
                  <div className="flex items-center justify-center gap-2">
                    <Crown size={16} className="text-yellow-600" />
                    <p className="text-gray-700 text-base font-medium">
                      {user.plan === 'free' ? 'Free Plan v0.946' : 'Pro Plan'}
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            </div>

            {/* Menu Buttons Section - Fixed at bottom with padding */}
            <div className="px-4 pb-24">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-white rounded-3xl shadow-lg mb-4"
              >
                {/* Menu Items */}
                <div className="bg-white rounded-3xl px-4 py-2">
                  {menuItems.map((item, index) => (
                    <motion.button
                      key={item.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7 + index * 0.1 }}
                      className={`w-full flex items-center justify-between py-4 px-2 text-left ${
                        index !== menuItems.length - 1 ? 'border-b border-border' : ''
                      }`}
                      onClick={() => handleMenuClick(item.id)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2.5 bg-gradient-to-br from-red-50 to-orange-50 rounded-xl text-primary">
                          {item.icon}
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{item.name}</p>
                          <p className="text-sm text-text-secondary">{item.description}</p>
                        </div>
                      </div>
                      <ChevronRight size={18} className="text-text-secondary" />
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>

          {/* Page Overlays - Mobile Only */}
          <AnimatePresence>
            {currentPage && renderPage()}
          </AnimatePresence>
        </>
      ) : (
        // Desktop version with working tabs
        <div className="max-w-4xl mx-auto">
          <div className="bg-card rounded-xl shadow-soft overflow-hidden">
            {/* Desktop header */}
            <div className="bg-primary-hover text-white p-6 pb-24 relative">
              <h1 className="text-2xl font-heading font-bold">Profile</h1>
              <p>Manage your account and preferences</p>
            </div>
            
            {/* Desktop Profile Card */}
            <div className="px-6 mb-6 -mt-20 relative z-20">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6"
              >
                <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                  <div className="relative">
                    <div className="h-32 w-32 flex items-center justify-center border-4 border-white rounded-xl bg-white/10">
                      <img 
                        src={user.avatar} 
                        alt={user.name}
                        className="h-26 w-26 object-contain"
                      />
                    </div>
                    <button 
                      className="absolute -bottom-3 -right-3 bg-primary text-white rounded-full p-2 hover:bg-primary/90 transition-colors flex items-center justify-center shadow-lg"
                      onClick={() => setIsAvatarSelectorOpen(true)}
                    >
                      <Camera size={20} />
                    </button>
                  </div>
                  
                  <div className="flex-1 text-center md:text-left mt-8">
                    <h2 className="text-xl font-heading font-semibold text-foreground">{user.name}</h2>
                    <p className="text-text-secondary text-sm mb-2">{user.plan === 'free' ? 'Free Plan' : 'Pro Plan'}</p>
                    

                    

                  </div>
                </div>
              </motion.div>
            </div>
            
            {/* Desktop Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <div className="flex">
                {['bodystats', 'preferences', 'settings', 'subscription', ...(user.isAdmin ? ['admin'] : [])].map((tab) => (
                  <button
                    key={tab}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                      selectedTab === tab
                        ? 'border-primary text-primary'
                        : 'border-transparent text-text-secondary hover:text-text-primary hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                    onClick={() => setSelectedTab(tab)}
                  >
                    {tab === 'bodystats' ? 'Body Stats & Goals' : 
                     tab === 'admin' ? 'Admin Panel' :
                     tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Desktop Tab Content */}
            <div className="p-6">
              {selectedTab === 'bodystats' && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-8 pb-8"
                >
                  {/* Desktop Body Stats Form */}
                  <DesktopBodyStatsForm />
                </motion.div>
              )}
              
              {selectedTab === 'preferences' && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-8 pb-8"
                >
                  {/* Dietary Preferences Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <div className="mb-6">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                          <span className="text-lg">🥗</span>
                        </div>
                        <h3 className="text-xl font-heading font-bold text-foreground">Dietary Preferences</h3>
                      </div>
                      <p className="text-text-secondary text-sm">
                        Select your dietary lifestyle choices to get personalized recipe suggestions
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                      {dietaryOptions.map((diet) => {
                        const isSelected = desktopDietaryPrefs.includes(diet.value);
                        return (
                          <motion.button
                            key={diet.value}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => toggleDesktopDietary(diet.value)}
                            className={`relative p-3 rounded-2xl border-2 transition-all duration-200 text-left ${
                              isSelected
                                ? diet.color + ' border-transparent shadow-md'
                                : 'bg-gray-100 dark:bg-gray-600 border-transparent shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-500'
                            }`}
                            onMouseEnter={(e) => handleDietaryMouseEnter(diet, e)}
                            onMouseLeave={handleDietaryMouseLeave}
                          >
                            <div className="flex items-center space-x-3">
                              {/* Icon */}
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                isSelected ? diet.iconBg : 'bg-gray-100 dark:bg-gray-700'
                              }`}>
                                <span className="text-lg">{diet.icon}</span>
                              </div>
                              
                              {/* Content */}
                              <div className="flex-1 text-left">
                                <h4 className={`font-semibold text-sm transition-colors duration-200 text-left ${
                                  isSelected ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-gray-100'
                                }`}>
                                  {diet.label}
                                </h4>
                              </div>
                              
                              {/* Checkbox indicator */}
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                isSelected 
                                  ? 'bg-white border-white shadow-sm' 
                                  : 'border-gray-300 dark:border-gray-600'
                              }`}>
                                {isSelected && (
                                  <div className="w-2.5 h-2.5 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full" />
                                )}
                              </div>
                            </div>
                            
                            {/* Selection indicator background */}
                            {isSelected && (
                              <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl pointer-events-none" />
                            )}
                          </motion.button>
                        );
                      })}
                    </div>
                  </motion.div>
                  
                  {/* Allergies Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="mb-6">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                          <span className="text-lg">⚠️</span>
                        </div>
                        <h3 className="text-xl font-heading font-bold text-foreground">Allergies & Restrictions</h3>
                      </div>
                      <p className="text-text-secondary text-sm">
                        Help us keep you safe by marking any food allergies or intolerances
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                      {[
                        { value: 'nuts', label: 'Nuts', icon: '🥜', color: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/30', iconBg: 'bg-red-100 dark:bg-red-800/30', textColor: 'text-red-700 dark:text-red-300' },
                        { value: 'shellfish', label: 'Shellfish', icon: '🦐', color: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/30', iconBg: 'bg-orange-100 dark:bg-orange-800/30', textColor: 'text-orange-700 dark:text-orange-300' },
                        { value: 'eggs', label: 'Eggs', icon: '🥚', color: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800/30', iconBg: 'bg-yellow-100 dark:bg-yellow-800/30', textColor: 'text-yellow-700 dark:text-yellow-300' },
                        { value: 'soy', label: 'Soy', icon: '🫘', color: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800/30', iconBg: 'bg-purple-100 dark:bg-purple-800/30', textColor: 'text-purple-700 dark:text-purple-300' },
                        { value: 'wheat', label: 'Wheat', icon: '🌾', color: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/30', iconBg: 'bg-amber-100 dark:bg-amber-800/30', textColor: 'text-amber-700 dark:text-amber-300' },
                        { value: 'fish', label: 'Fish', icon: '🐠', color: 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800/30', iconBg: 'bg-cyan-100 dark:bg-cyan-800/30', textColor: 'text-cyan-700 dark:text-cyan-300' },
                        { value: 'milk', label: 'Milk', icon: '🥛', color: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/30', iconBg: 'bg-blue-100 dark:bg-blue-800/30', textColor: 'text-blue-700 dark:text-blue-300' },
                        { value: 'peanuts', label: 'Peanuts', icon: '🥜', color: 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800/30', iconBg: 'bg-rose-100 dark:bg-rose-800/30', textColor: 'text-rose-700 dark:text-rose-300' }
                      ].map((allergy) => {
                        const isSelected = desktopAllergies.includes(allergy.value);
                        return (
                          <motion.button
                            key={allergy.value}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => toggleDesktopAllergy(allergy.value)}
                            className={`relative p-3 rounded-2xl border-2 transition-all duration-200 text-left ${
                              isSelected
                                ? allergy.color + ' border-transparent shadow-md'
                                : 'bg-gray-100 dark:bg-gray-600 border-transparent shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-500'
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              {/* Icon */}
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                isSelected ? allergy.iconBg : 'bg-gray-100 dark:bg-gray-700'
                              }`}>
                                <span className="text-lg">{allergy.icon}</span>
                              </div>
                              
                              {/* Content */}
                              <div className="flex-1 text-left">
                                <h4 className={`font-semibold text-sm transition-colors duration-200 text-left ${
                                  isSelected ? allergy.textColor : 'text-foreground'
                                }`}>
                                  {allergy.label}
                                </h4>
                              </div>
                              
                              {/* Checkbox indicator */}
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                isSelected 
                                  ? 'bg-white border-white shadow-sm' 
                                  : 'border-gray-300 dark:border-gray-600'
                              }`}>
                                {isSelected && (
                                  <div className="w-2.5 h-2.5 bg-gradient-to-r from-red-500 to-red-600 rounded-full" />
                                )}
                              </div>
                            </div>
                            
                            {/* Selection indicator background */}
                            {isSelected && (
                              <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl pointer-events-none" />
                            )}
                          </motion.button>
                        );
                      })}
                      
                      {/* Custom allergies */}
                      {customAllergies.map((customAllergy) => {
                        const isSelected = desktopAllergies.includes(customAllergy);
                        return (
                          <CustomAllergyCard
                            key={customAllergy}
                            allergyName={customAllergy}
                            isSelected={isSelected}
                            onToggle={() => toggleDesktopAllergy(customAllergy)}
                            onRemove={() => handleRemoveCustomAllergy(customAllergy)}
                          />
                        );
                      })}
                      
                      {/* Add Custom Allergy Button - Desktop */}
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleDesktopAddCustomAllergy}
                        className="relative p-3 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-all duration-200 text-left bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800"
                      >
                        <div className="flex items-center justify-center h-full">
                          <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                            <span className="text-xl text-gray-500 dark:text-gray-400">+</span>
                          </div>
                        </div>
                      </motion.button>
                    </div>
                  </motion.div>

                  {/* Summary Card */}
                  {(desktopDietaryPrefs.length > 0 || desktopAllergies.length > 0) && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 mb-8"
                    >
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                          <span className="text-lg">✨</span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Your Preferences</h3>
                      </div>
                      
                      <div className="space-y-3">
                        {user.preferences.length > 0 && (
                  <div>
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Dietary Preferences:</p>
                            <div className="flex flex-wrap gap-2">
                              {user.preferences.map((pref) => {
                                const option = dietaryOptions.find(opt => opt.value === pref);
                                return (
                                  <span 
                                    key={pref} 
                                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-800/30 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-700/50"
                                  >
                                    <span className="mr-1">{option?.icon}</span>
                                    {option?.label}
                                  </span>
                                );
                              })}
                    </div>
                  </div>
                        )}
                  
                        {user.allergies.length > 0 && (
                  <div>
                            <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">Allergies & Restrictions:</p>
                            <div className="flex flex-wrap gap-2">
                              {user.allergies.map((allergy) => {
                                const option = allergyOptions.find(opt => opt.value === allergy);
                                // If it's a custom allergy (not found in allergyOptions), display it with warning icon
                                const displayName = option?.label || allergy;
                                const displayIcon = option?.icon || '⚠️';
                                
                                return (
                                  <span 
                                    key={allergy} 
                                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-800/30 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-700/50"
                                  >
                                    <span className="mr-1">{displayIcon}</span>
                                    {displayName}
                                  </span>
                                );
                              })}
                    </div>
                          </div>
                        )}
                  </div>
                    </motion.div>
                  )}

                  {/* Save Button - REMOVED for auto-save functionality */}
                </motion.div>
              )}
              
              {selectedTab === 'settings' && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div>
                    <h3 className="text-lg font-medium mb-4">Account Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Name</label>
                        <input
                          type="text"
                          value={desktopEditableName}
                          onChange={handleDesktopNameChange}
                          onBlur={handleDesktopNameBlur}
                          onKeyPress={handleDesktopNameKeyPress}
                          disabled={isDesktopNameUpdating}
                          className="w-full p-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-background text-foreground focus:border-primary focus:ring-1 focus:ring-primary transition-colors disabled:opacity-50"
                          placeholder="Enter your name"
                        />
                        {isDesktopNameUpdating && (
                          <p className="text-xs text-text-secondary mt-1">Updating...</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Email</label>
                        <input
                          type="email"
                          value={user.email}
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                          readOnly
                        />
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Email cannot be changed</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Feedback Section */}
                  <div>
                    <motion.div
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      className="transition-all duration-200"
                    >
                      <a
                        href="https://forms.gle/i4TvhV6oXs7v8TJ77"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full flex items-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all duration-200"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
                            <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="text-left">
                            <p className="font-medium">Send Feedback</p>
                            <p className="text-sm text-blue-600 dark:text-blue-500">Help us improve your experience</p>
                          </div>
                        </div>
                        <ExternalLink className="h-4 w-4 text-blue-500 dark:text-blue-400 ml-auto" />
                      </a>
                    </motion.div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium mb-4">General Settings</h3>
                    <div className="space-y-3">
                      <div className="flex items-center p-4 bg-muted rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Globe className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">Language</p>
                            <p className="text-sm text-text-secondary">{(() => {
                              try {
                                const language = navigator.language || navigator.languages?.[0] || 'en-US';
                                const languageNames = new Intl.DisplayNames([language], { type: 'language' });
                                const languageName = languageNames.of(language);
                                return `${languageName} (${language})`;
                              } catch (error) {
                                return 'English (US)';
                              }
                            })()}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center p-4 bg-muted rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Clock className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">Time Zone</p>
                            <p className="text-sm text-text-secondary">{(() => {
                              try {
                                const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                                const now = new Date();
                                const timeString = now.toLocaleTimeString('en-US', { 
                                  hour: 'numeric', 
                                  minute: '2-digit',
                                  timeZoneName: 'short'
                                });
                                return `${timeZone} (${timeString})`;
                              } catch (error) {
                                return 'Auto-detect';
                              }
                            })()}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Scale className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">Measurement System</p>
                            <p className="text-sm text-text-secondary">
                              {user.measurementSystem === 'metric' ? 'Metric (kg, g, l, ml)' : 'Imperial (lbs, oz, gal, fl oz)'}
                            </p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => useNoshStore.getState().updateUserMeasurementSystem('metric')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-full border-2 transition-all duration-200 ${
                              user.measurementSystem === 'metric'
                                ? 'bg-primary text-white border-primary shadow-sm'
                                : 'bg-card text-text-secondary border-border hover:border-primary/50 hover:text-text-primary'
                            }`}
                          >
                            Metric
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => useNoshStore.getState().updateUserMeasurementSystem('imperial')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-full border-2 transition-all duration-200 ${
                              user.measurementSystem === 'imperial'
                                ? 'bg-primary text-white border-primary shadow-sm'
                                : 'bg-card text-text-secondary border-border hover:border-primary/50 hover:text-text-primary'
                            }`}
                          >
                            Imperial
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium mb-4">Notification Settings</h3>
                    <div className="space-y-3">
                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        className="flex items-center justify-between p-4 bg-muted rounded-lg transition-all duration-200"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Bell className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">Expiry Notifications</p>
                            <p className="text-sm text-muted-foreground">
                              {isSupported 
                                ? "Get alerted when food is about to expire" 
                                : "Not supported in this browser"}
                            </p>
                            {!hasPermission && isSupported && (
                              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                Permission required
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {preferences && (
                            <button
                              onClick={testNotification}
                              disabled={notificationLoading}
                              className="px-2 py-1 text-xs bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors disabled:opacity-50"
                            >
                              Test
                            </button>
                          )}
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={preferences?.expiry_notifications_enabled || false}
                              onChange={(e) => toggleExpiryNotifications(e.target.checked)}
                              disabled={!isSupported || notificationLoading}
                              className="sr-only peer" 
                            />
                            <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary peer-disabled:opacity-50"></div>
                          </label>
                        </div>
                      </motion.div>
                      
                      <motion.div
                        whileHover={{ scale: 1.01 }}
                        className="flex items-center justify-between p-4 bg-muted rounded-lg transition-all duration-200"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Star className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">Daily Summary</p>
                            <p className="text-sm text-muted-foreground">
                              {isSupported 
                                ? "Daily overview of your food inventory" 
                                : "Not supported in this browser"}
                            </p>
                          </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={preferences?.daily_summary_enabled || false}
                            onChange={(e) => toggleDailySummary(e.target.checked)}
                            disabled={!isSupported || notificationLoading}
                            className="sr-only peer" 
                          />
                          <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary peer-disabled:opacity-50"></div>
                        </label>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              )}
              
              {selectedTab === 'subscription' && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {/* AI Usage Section */}
                  <div className="mb-8">
                    <AIUsageSection />
                  </div>
                  
                  <div className="text-center mb-6">
                    <Crown className="h-12 w-12 mx-auto text-accent mb-2" />
                    <h3 className="text-xl font-heading font-semibold">Upgrade to Nosh Pro</h3>
                    <p className="text-text-secondary">Unlock premium features and get more out of Nosh</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-muted rounded-xl p-6">
                      <h4 className="font-heading font-semibold mb-4">Free Plan</h4>
                      <p className="text-2xl font-bold mb-4">$0 <span className="text-sm font-normal text-text-secondary">/month</span></p>
                      <ul className="space-y-2 mb-6">
                        <li className="flex items-start">
                          <BadgeCheck size={18} className="text-secondary mr-2 mt-0.5" />
                          <span>Track up to 50 food items</span>
                        </li>
                        <li className="flex items-start">
                          <BadgeCheck size={18} className="text-secondary mr-2 mt-0.5" />
                          <span>10 recipe generations per month</span>
                        </li>
                        <li className="flex items-start">
                          <BadgeCheck size={18} className="text-secondary mr-2 mt-0.5" />
                          <span>10 meal logs per month</span>
                        </li>
                        <li className="flex items-start">
                          <BadgeCheck size={18} className="text-secondary mr-2 mt-0.5" />
                          <span>Manual inventory entry only</span>
                        </li>
                        <li className="flex items-start">
                          <BadgeCheck size={18} className="text-secondary mr-2 mt-0.5" />
                          <span>Basic meal planner</span>
                        </li>
                        <li className="flex items-start">
                          <BadgeCheck size={18} className="text-secondary mr-2 mt-0.5" />
                          <span>Basic Calorie Tracker</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div className="bg-primary/5 border-2 border-primary rounded-xl p-6 relative">
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-primary text-white px-3 py-1 rounded-full text-xs font-medium">
                          Most Popular
                        </span>
                      </div>
                      <h4 className="font-heading font-semibold mb-4">Pro Plan</h4>
                      <p className="text-2xl font-bold mb-4">$15 <span className="text-sm font-normal text-text-secondary">/month</span></p>
                      <ul className="space-y-2 mb-6">
                        <li className="flex items-start">
                          <BadgeCheck size={18} className="text-primary mr-2 mt-0.5" />
                          <span>Unlimited inventory tracking</span>
                        </li>
                        <li className="flex items-start">
                          <BadgeCheck size={18} className="text-primary mr-2 mt-0.5" />
                          <span>Unlimited photo/receipt scanning</span>
                        </li>
                        <li className="flex items-start">
                          <BadgeCheck size={18} className="text-primary mr-2 mt-0.5" />
                          <span>AI recipe generation (~200/month)</span>
                        </li>
                        <li className="flex items-start">
                          <BadgeCheck size={18} className="text-primary mr-2 mt-0.5" />
                          <span>Unlimited meal logs</span>
                        </li>
                        <li className="flex items-start">
                          <BadgeCheck size={18} className="text-primary mr-2 mt-0.5" />
                          <span>Unlimited meal planning</span>
                        </li>
                        <li className="flex items-start">
                          <BadgeCheck size={18} className="text-primary mr-2 mt-0.5" />
                          <span>unlimited shopping list</span>
                        </li>
                        <li className="flex items-start">
                          <BadgeCheck size={18} className="text-primary mr-2 mt-0.5" />
                          <span>Personalized calorie tracker</span>
                        </li>
                      </ul>
                      <Button className="w-full">
                        Upgrade to Pro
                      </Button>
                    </div>
                  </div>
                  
                  <div className="bg-muted rounded-xl p-4 text-center">
                    <p className="text-sm text-text-secondary mb-2">
                      🔒 30-day money back guarantee
                    </p>
                    <p className="text-xs text-text-muted">
                      Cancel anytime. No hidden fees.
                    </p>
                  </div>
                </motion.div>
              )}

              {selectedTab === 'admin' && user.isAdmin && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-8 pb-8"
                >
                  {/* Hackathon Mode Section - Admin Only */}
                  <div>
                    <h3 className="text-lg font-medium mb-4">🎯 Hackathon Mode</h3>
                    <CaptchaToggle isMobile={false} />
                  </div>

                  {/* Announcement Management */}
                  <div>
                    <h3 className="text-lg font-medium mb-4">📢 Announcement Management</h3>
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                      <div className="space-y-4">
                        {/* Current Status */}
                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-gray-100">Current Status</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {announcementConfigManager.shouldShowAnnouncement() ? 
                                `Active: ${currentAnnouncementConfig.title}` : 
                                'No active announcement'
                              }
                              {announcementConfigManager.shouldShowAnnouncement() && announcementConfigManager.isAnnouncementDismissed() && (
                                <span className="text-xs text-orange-600 dark:text-orange-400 ml-2">(dismissed by you)</span>
                              )}
                            </p>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                            announcementConfigManager.shouldShowAnnouncement() 
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}>
                            {announcementConfigManager.shouldShowAnnouncement() ? 'LIVE' : 'INACTIVE'}
                          </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="grid grid-cols-2 gap-3">
                          <Button 
                            variant="outline" 
                            className="h-24 flex-col gap-2"
                            onClick={() => setIsAnnouncementEditorOpen(true)}
                          >
                            <Settings size={20} />
                            <span className="text-sm">Edit Content</span>
                          </Button>
                          <Button 
                            variant="outline" 
                            className="h-24 flex-col gap-2"
                            onClick={handlePreviewAnnouncement}
                          >
                            <ExternalLink size={20} />
                            <span className="text-sm">Preview</span>
                          </Button>
                        </div>

                        {/* Current Content Preview */}
                        {announcementConfigManager.shouldShowAnnouncement() && (
                          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                            <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                              {currentAnnouncementConfig.title}
                            </h5>
                            <div className="space-y-2">
                              {currentAnnouncementConfig.items.map((item: AnnouncementItem, index: number) => (
                                <div key={item.id} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                                  <span>{item.icon || '•'}</span>
                                  <span>{item.text}</span>
                                </div>
                              ))}
                            </div>
                            <div className="mt-3 text-xs text-gray-500 dark:text-gray-500">
                              Version: {currentAnnouncementConfig.version} • 
                              Updated: {announcementUtils.formatDate(currentAnnouncementConfig.updatedAt)}
                            </div>
                          </div>
                        )}

                        {/* Instructions */}
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-4">
                          <h5 className="font-medium text-blue-900 dark:text-blue-100 mb-2">💡 Quick Guide</h5>
                          <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                            <li>• Click "Edit Content" to modify announcements</li>
                            <li>• Use "Preview" to see how it looks to users</li>
                            <li>• Changes take effect immediately</li>
                            <li>• Keep content short and engaging</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <AdminAIUsagePanel className="w-full" />
                </motion.div>
              )}
            </div>
          </div>

          {/* Desktop Avatar Selector Modal */}
          <AvatarSelector
            currentAvatar={user.avatar}
            onSelect={handleAvatarUpdate}
            onClose={() => setIsAvatarSelectorOpen(false)}
            isOpen={isAvatarSelectorOpen}
          />
        </div>
      )}

      {/* Mobile Avatar Selector Portal - Renders to document.body */}
      {createMobileAvatarPortal()}

      {/* Desktop Dietary Preferences Tooltip Portal */}
      {showDietaryTooltip && !isMobile && dietaryTooltipData && ReactDOM.createPortal(
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: dietaryTooltipData.buttonRect.bottom + 8,
            left: Math.max(16, Math.min(window.innerWidth - 288, dietaryTooltipData.buttonRect.left + (dietaryTooltipData.buttonRect.width / 2) - 144)),
            zIndex: 10000
          }}
        >
          {/* Arrow pointing up - positioned relative to button center */}
          <div 
            className="w-3 h-3 bg-white border-l border-t border-gray-200 transform rotate-45 absolute -top-1.5 shadow-sm"
            style={{
              left: Math.max(16, Math.min(272, dietaryTooltipData.buttonRect.left + (dietaryTooltipData.buttonRect.width / 2) - (Math.max(16, Math.min(window.innerWidth - 288, dietaryTooltipData.buttonRect.left + (dietaryTooltipData.buttonRect.width / 2) - 144)))))
            }}
          ></div>
          
          {/* Tooltip content */}
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-4 w-72 relative">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${dietaryTooltipData.diet.iconBg}`}>
                  <span className="text-sm">{dietaryTooltipData.diet.icon}</span>
                </div>
                <h3 className="font-semibold text-gray-900 text-sm">{dietaryTooltipData.diet.label}</h3>
              </div>
              
              <div className="text-gray-600 text-sm leading-relaxed">
                {dietaryTooltipData.diet.tooltip?.split('\n').map((line: string, index: number) => (
                  <p key={index} className={index > 0 ? 'mt-2' : ''}>
                    {line}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </motion.div>,
        document.body
      )}
      
      {/* Desktop Add Custom Allergy Modal */}
      {showDesktopAddAllergyModal && !isMobile && ReactDOM.createPortal(
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[300]"
            onClick={handleDesktopCancelCustomAllergy}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[301] flex items-center justify-center p-4"
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Add Custom Allergy
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Enter the name of your custom allergy or food restriction.
                </p>
                
                <input
                  type="text"
                  value={desktopNewAllergyName}
                  onChange={(e) => setDesktopNewAllergyName(e.target.value)}
                  placeholder="e.g., Sesame, Gluten, etc."
                  className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoFocus
                  onKeyPress={(e) => e.key === 'Enter' && handleDesktopSaveCustomAllergy()}
                />
                
                <div className="flex space-x-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={handleDesktopCancelCustomAllergy}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDesktopSaveCustomAllergy}
                    disabled={!desktopNewAllergyName.trim()}
                    className="flex-1"
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </>,
        document.body
      )}

      {/* Announcement Editor Modal */}
      <AnnouncementEditorModal
        isOpen={isAnnouncementEditorOpen}
        onClose={() => setIsAnnouncementEditorOpen(false)}
        onSave={handleSaveAnnouncement}
      />

    </>
  );
};

// Custom Allergy Card component
const CustomAllergyCard = ({ 
  allergyName, 
  isSelected, 
  onToggle, 
  onRemove 
}: { 
  allergyName: string, 
  isSelected: boolean, 
  onToggle: () => void,
  onRemove: () => void
}) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    className={`relative p-3 rounded-2xl border-2 transition-all duration-200 text-left w-full ${
      isSelected
        ? 'bg-orange-50 dark:bg-orange-900/20 border-transparent shadow-md'
        : 'bg-gray-100 dark:bg-gray-600 border-transparent shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-500'
    }`}
  >
    {/* Remove button */}
    <div
      onClick={(e) => {
        e.stopPropagation();
        onRemove();
      }}
      style={{
        position: 'absolute',
        top: '-6px',
        right: '-6px',
        width: '20px',
        height: '20px',
        minWidth: '20px',
        minHeight: '20px',
        maxWidth: '20px',
        maxHeight: '20px',
        backgroundColor: '#ef4444',
        color: 'white',
        borderRadius: '50%',
        border: '1px solid white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '11px',
        lineHeight: '1',
        cursor: 'pointer',
        zIndex: 30,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        transition: 'background-color 0.2s',
        padding: '0',
        margin: '0',
        boxSizing: 'border-box',
        flexShrink: 0,
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
    >
      ×
    </div>
    
    <button
      onClick={onToggle}
      className="w-full"
    >
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
          isSelected ? 'bg-orange-100 dark:bg-orange-800/30' : 'bg-gray-100 dark:bg-gray-700'
        }`}>
          <span className="text-lg">⚠️</span>
        </div>
        
        {/* Content */}
        <div className="flex-1 text-left">
          <h3 className={`font-semibold text-sm transition-colors duration-200 text-left ${
            isSelected ? 'text-orange-700 dark:text-orange-300' : 'text-gray-900 dark:text-gray-100'
          }`}>
            {allergyName}
          </h3>
        </div>
        
        {/* Checkbox indicator */}
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
          isSelected 
            ? 'bg-white border-white shadow-sm' 
            : 'border-gray-300 dark:border-gray-600'
        }`}>
          {isSelected && (
            <div className="w-2.5 h-2.5 bg-gradient-to-r from-orange-500 to-red-500 rounded-full" />
          )}
        </div>
      </div>
      
      {/* Selection indicator background */}
      {isSelected && (
        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl pointer-events-none" />
      )}
    </button>
  </motion.div>
);

export default Profile;